import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogOut, User, Building, Hotel, ShoppingCart, ArrowLeft, Calendar, MapPin, Clock, X, CreditCard, FileText, Download, Mail, Phone, Star, Filter, ChevronDown, Bed, Users, Search, DollarSign, ChevronLeft, ChevronRight, Settings, XCircle, CheckCircle, Compass, Plane } from 'lucide-react'
import bookingService from '../services/bookingService'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import MainLayout from '../components/Layout/MainLayout'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'
import { jsPDF } from 'jspdf'
import { toast } from 'react-hot-toast'
import { getHotelStaySourcePresentation } from '../utils/staySourceLabel'
import { getHotelBookingPriceBreakdown } from '../utils/hotelBookingPricing'
import HotelBookingPriceBreakdown from '../components/booking/HotelBookingPriceBreakdown'

// Utility to load image and handle CORS for PDF generation
const getImageDataUrl = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    const cacheBuster = `t=${new Date().getTime()}`;
    const separator = url.includes('?') ? '&' : '?';
    img.src = `${url}${separator}${cacheBuster}`;
  });
};

const UserBookingPage = () => {
  const [bookingsData, setBookingsData] = useState({
    bookings: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasNext: false,
      hasPrev: false
    }
  })
  const [isLoading, setIsLoading] = useState(true)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [cancellingBookingId, setCancellingBookingId] = useState(null)
  const [generatingVoucherId, setGeneratingVoucherId] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [selectedConfirmationForCancel, setSelectedConfirmationForCancel] = useState(null)
  const [cancellationReason, setCancellationReason] = useState('')
  const [cancellationReasontwo, setCancellationReasontwo] = useState('')
  const [environmentImpacts, setEnvironmentImpacts] = useState({})

  // White Label Modal states
  const [showWhiteLabelModal, setShowWhiteLabelModal] = useState(false)
  const [selectedBookingForWhiteLabel, setSelectedBookingForWhiteLabel] = useState(null)
  const [serviceCharge, setServiceCharge] = useState('')
  const [generatingWhiteLabel, setGeneratingWhiteLabel] = useState(false)

  // Cancellation loader states
  const [showCancellationLoader, setShowCancellationLoader] = useState(false)
  const [cancellationProgress, setCancellationProgress] = useState(0)
  const [cancellationCurrentStep, setCancellationCurrentStep] = useState(0)
  const [cancellationMessage, setCancellationMessage] = useState('')

  const cancellationSteps = [
    'Processing cancellation request',
    'Verifying booking details',
    'Applying cancellation policy',
    'Processing refund if applicable',
    'Updating booking status'
  ]

  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(10)
  const [searchInput, setSearchInput] = useState('') // what user types (no refresh on keystroke)
  const [searchQuery, setSearchQuery] = useState('') // applied search (sent to API on button/Enter)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedHotelStayKind, setSelectedHotelStayKind] = useState('all') // all | off_market | gds (hotel lines only)
  const [selectedType, setSelectedType] = useState('all') // all | hotel | experience
  const [showFilters, setShowFilters] = useState(true)
  const [profile, setProfile] = useState(null)
  const [isProfileLoading, setIsProfileLoading] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    loadBookingDetails()
    loadAgentProfile()
  }, [currentPage, searchQuery, selectedMonth, selectedStatus, selectedType, selectedHotelStayKind])

  const loadAgentProfile = async () => {
    try {
      setIsProfileLoading(true)
      const response = await travelAgentService.getProfile()
      if (response.data) {
        setProfile(response.data)
      }
    } catch (error) {
      console.error('Failed to load agent profile:', error)
    } finally {
      setIsProfileLoading(false)
    }
  }

  const loadBookingDetails = async () => {
    try {
      setIsLoading(true)
      // Build query parameters
      const baseParams = {
        page: currentPage,
        limit: limit
      }

      if (searchQuery.trim()) {
        baseParams.search = searchQuery.trim()
      }
      if (selectedMonth) {
        baseParams.month = selectedMonth
      }
      if (selectedStatus !== 'all') {
        baseParams.status = selectedStatus
      }
      if (selectedHotelStayKind !== 'all' && (selectedType === 'hotel' || selectedType === 'all')) {
        baseParams.stayKind = selectedHotelStayKind
      }

      let rawHotelData = null
      let rawExperienceData = null

      if (selectedType === 'experience') {
        const result = await bookingService.getUserExperienceBookings(baseParams)
        rawExperienceData = result.data || null
      } else if (selectedType === 'hotel') {
        const result = await bookingService.getUserBookings(baseParams)
        rawHotelData = result.data || null
      } else {
        // "All" tab: load hotel + experience separately and merge client-side
        const [hotelRes, expRes] = await Promise.all([
          bookingService.getUserBookings(baseParams),
          bookingService.getUserExperienceBookings(baseParams)
        ])
        rawHotelData = hotelRes.data || null
        rawExperienceData = expRes.data || null
      }

      const fallbackPagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNext: false,
        hasPrev: false
      }

      let rawBookings = []
      let pagination = fallbackPagination

      if (selectedType === 'experience') {
        const rawData = rawExperienceData || { bookings: [], pagination: fallbackPagination }
        rawBookings = rawData.bookings || []
        pagination = rawData.pagination || fallbackPagination
      } else if (selectedType === 'hotel') {
        const rawData = rawHotelData || { bookings: [], pagination: fallbackPagination }
        rawBookings = rawData.bookings || []
        pagination = rawData.pagination || fallbackPagination
      } else {
        const hotelData = rawHotelData || { bookings: [], pagination: fallbackPagination }
        const expData = rawExperienceData || { bookings: [], pagination: fallbackPagination }

        const hotelBookings = (hotelData.bookings || []).map(item => ({ kind: 'hotel', ...item }))
        const experienceBookings = (expData.bookings || []).map(item => ({ kind: 'experience', ...item }))

        // Merge and sort by createdAt descending
        rawBookings = [...hotelBookings, ...experienceBookings].sort((a, b) => {
          const getDate = (bk) => bk.createdAt || bk.created_at || bk.timestamp || null
          const da = getDate(a) ? new Date(getDate(a)).getTime() : 0
          const db = getDate(b) ? new Date(getDate(b)).getTime() : 0
          return db - da
        })

        const totalCount = (hotelData.pagination?.totalCount || 0) + (expData.pagination?.totalCount || 0)
        const totalPages = Math.max(hotelData.pagination?.totalPages || 1, expData.pagination?.totalPages || 1)

        pagination = {
          currentPage,
          totalPages,
          totalCount,
          hasNext: currentPage < totalPages,
          hasPrev: currentPage > 1
        }
      }

      // Normalise bookings so each entry has a kind field
      let normalizedBookings = []

      if (selectedType === 'experience') {
        normalizedBookings = rawBookings.map(item => ({ kind: 'experience', ...item }))
      } else if (selectedType === 'hotel') {
        normalizedBookings = rawBookings.map(item => ({ kind: 'hotel', ...item }))
      } else {
        // "All" already has kind set when we merged
        normalizedBookings = rawBookings
      }

      setBookingsData({
        bookings: normalizedBookings,
        pagination
      })

      // Environment impact currently applies only to hotel bookings
      if (selectedType === 'hotel' || selectedType === 'all') {
        const hotelOnly = normalizedBookings.filter(b => b.kind !== 'experience')
        await loadEnvironmentImpactData(hotelOnly || [])
      }
    } catch (error) {
      console.error('Failed to load bookings:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to load bookings' })
    } finally {
      setIsLoading(false)
    }
  }


  const loadEnvironmentImpactData = async (bookings) => {
    try {
      const impacts = {}

      // Load environment impact for each booking
      for (const booking of bookings) {
        try {
          const result = await bookingService.getEnvironmentImpact(booking.confirmationId)
          if (result.data) {
            impacts[booking.confirmationId] = result.data
          }
        } catch (error) {
          console.warn(`No environment data for booking ${booking.confirmationId}:`, error.message)
          // Set default values if no environment data found
          impacts[booking.confirmationId] = {
            electricityValue: 0,
            carbonEmission: 0,
            tier: 'Not Available'
          }
        }
      }

      setEnvironmentImpacts(impacts)
    } catch (error) {
      console.error('Failed to load environment impact data:', error)
    }
  }


  const getEnvironmentImpact = (booking) => {
    return environmentImpacts[booking.confirmationId] || {
      electricityValue: 0,
      carbonEmission: 0,
      tier: 'Not Available'
    }
  }

  const applySearch = () => {
    setSearchQuery(searchInput.trim())
    setCurrentPage(1)
  }

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value)
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applySearch()
    }
  }

  const clearSearchInput = () => {
    setSearchInput('')
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value)
    setCurrentPage(1)
  }

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value)
    setCurrentPage(1)
  }

  const applyTypeFilter = (value) => {
    setSelectedType(value)
    if (value === 'experience') {
      setSelectedHotelStayKind('all')
    }
    setCurrentPage(1)
  }

  const handleTypeChange = (e) => {
    applyTypeFilter(e.target.value)
  }

  const clearAllFilters = () => {
    setSearchInput('')
    setSearchQuery('')
    setSelectedMonth('')
    setSelectedStatus('all')
    setSelectedType('all')
    setSelectedHotelStayKind('all')
    setCurrentPage(1)
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleLogout = () => {
    travelAgentService.logout()
    navigate('/login')
  }

  const openCancelModal = (booking, newConfirmationId) => {
    setSelectedBooking(booking)
    setSelectedConfirmationForCancel(newConfirmationId)
    setCancellationReason('')
    setCancellationReasontwo('')
    setShowCancelModal(true)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setSelectedBooking(null)
    setSelectedConfirmationForCancel(null)
    setCancellationReason('')
    setCancellationReasontwo('')
    setShowCancellationLoader(false)
    setCancellationProgress(0)
    setCancellationCurrentStep(0)
    setCancellationMessage('')
  }

  const openWhiteLabelModal = (booking) => {
    setSelectedBookingForWhiteLabel(booking)
    setServiceCharge('')
    setShowWhiteLabelModal(true)
  }

  const closeWhiteLabelModal = () => {
    setShowWhiteLabelModal(false)
    setSelectedBookingForWhiteLabel(null)
    setServiceCharge('')
    setGeneratingWhiteLabel(false)
  }

  const handleCancelBooking = async () => {
    if (selectedConfirmationForCancel !== cancellationReason) {
      toast.error(selectedBooking?.kind === 'experience' ? 'Please type booking reference correctly' : 'Please type confirmation ID correctly')
      return
    }

    if (!cancellationReasontwo.trim()) {
      toast.error('Please provide a cancellation reason')
      return
    }

    setCancellingBookingId(selectedConfirmationForCancel)
    setShowCancellationLoader(true)
    setCancellationProgress(0)
    setCancellationCurrentStep(0)
    setCancellationMessage(cancellationSteps[0])

    try {
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setCancellationProgress(prev => {
          const newProgress = prev + Math.random() * 10 + 5
          const currentProgress = Math.min(newProgress, 85) // Don't go to 100 until actual completion

          // Update steps based on progress
          if (currentProgress >= 20 && cancellationCurrentStep === 0) {
            setCancellationCurrentStep(1)
            setCancellationMessage(cancellationSteps[1])
          } else if (currentProgress >= 40 && cancellationCurrentStep === 1) {
            setCancellationCurrentStep(2)
            setCancellationMessage(cancellationSteps[2])
          } else if (currentProgress >= 60 && cancellationCurrentStep === 2) {
            setCancellationCurrentStep(3)
            setCancellationMessage(cancellationSteps[3])
          } else if (currentProgress >= 80 && cancellationCurrentStep === 3) {
            setCancellationCurrentStep(4)
            setCancellationMessage(cancellationSteps[4])
          }

          return currentProgress
        })
      }, 400)

      // Perform the actual cancellation (experience cancel commented out)
      if (selectedBooking?.kind === 'experience') {
        // Experience Cancel Booking - commented out
        // await bookingService.cancelExperienceBooking(
        //   selectedBooking.bookingReference,
        //   cancellationReasontwo
        // )
        clearInterval(progressInterval)
        setShowCancellationLoader(false)
        closeCancelModal()
        toast.error('Experience cancellation is currently disabled.')
        return
      }
      await bookingService.cancelBooking(
        selectedConfirmationForCancel,
        cancellationReasontwo
      )

      // Clear interval and complete progress
      clearInterval(progressInterval)
      setCancellationProgress(100)
      setCancellationCurrentStep(4)
      setCancellationMessage('Cancellation completed successfully')

      // Wait a moment to show completion
      setTimeout(() => {
        const successMessage = `Booking ${selectedConfirmationForCancel} has been cancelled successfully.`
        setMessage({ type: 'success', text: successMessage })
        toast.success(successMessage)
        closeCancelModal()
        setShowCancellationLoader(false)
        // Reload bookings to reflect the cancellation
        loadBookingDetails()
      }, 1500)
    } catch (error) {
      console.error('Failed to cancel booking:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to cancel booking' })
      toast.error(error.message || 'Failed to cancel booking')
      setShowCancellationLoader(false)
    } finally {
      setCancellingBookingId(null)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC' // Add this
    })
  }

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount)
  }

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase()
    const statusConfig = {
      confirmed: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Confirmed' },
      cancelled: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Cancelled' },
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pending' },
      completed: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Completed' }
    }
    const config = statusConfig[s] || { color: 'bg-slate-100 text-slate-800 border-slate-200', label: (status && String(status).trim()) ? String(status) : 'Pending' }
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <span className="w-2 h-2 rounded-full bg-current mr-2"></span>
        {config.label}
      </span>
    )
  }

  const getHotelStripeTiming = (booking) => {
    const bd = booking?.bookingData || {}
    const paymentBreakdown = bd?.paymentBreakdown || null
    const scheduledPayment = bd?.scheduledPayment || null
    const immediatePI = bd?.immediatePaymentIntent || null

    const currency =
      paymentBreakdown?.currency ||
      booking?.generalBackupMetadata?.hotelTotals?.currencyCode ||
      booking?.bookingData?.originalCartItem?.currency ||
      'USD'

    const scheduledStatus = (scheduledPayment?.status || '').toString().toLowerCase()
    const isScheduled = scheduledStatus === 'pending'

    if (isScheduled) {
      return {
        label: 'Scheduled (Charge later)',
        badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
        amount: scheduledPayment?.amount,
        currency,
        dueAt: scheduledPayment?.dueAt,
        status: scheduledPayment?.status || 'pending'
      }
    }

    // Immediate (or not_required) → treat as paid now.
    return {
      label: 'Paid (Immediate)',
      badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      amount: paymentBreakdown?.totalPayable,
      currency,
      dueAt: null,
      status: immediatePI?.status || 'succeeded',
      paymentIntentId: immediatePI?.paymentIntentId || immediatePI?.id || null
    }
  }

  const getCancellationPolicy = (cartItem) => {
    try {
      // Check for cancellationPolicy array
      if (cartItem?.cancellationPolicy?.[0]?.Deadline) {
        const deadline = cartItem.cancellationPolicy[0].Deadline;
        const multiplier = deadline.OffsetUnitMultiplier;
        const timeUnit = deadline.OffsetTimeUnit;
        const offsetDropTime = deadline.OffsetDropTime;

        // If all required fields are present, format them
        if (multiplier && timeUnit && offsetDropTime) {
          // Split by capital letters and join with spaces
          const formattedOffsetDropTime = offsetDropTime.split(/(?=[A-Z])/).join(' ');

          return `${multiplier} ${timeUnit} ${formattedOffsetDropTime}`.trim();
        }
      }

      // Fallback to rateDetails cancellation policy
      if (cartItem?.rateDetails?.cancellationPolicy?.CancelPenalty?.[0]) {
        const penalty = cartItem.rateDetails.cancellationPolicy.CancelPenalty[0];
        return penalty.Refundable ? 'Free cancellation available until check-in' : 'Non-refundable booking';
      }

      // Final fallback
      return 'Before Check-in';

    } catch (error) {
      console.error('Error getting cancellation policy:', error);
      return 'Before Check-in';
    }
  };

  const getDepositePolicy = (cartItem) => {
    if (cartItem?.cancellationPolicy) {
      return cartItem.cancellationPolicy
    }

    if (cartItem?.rateDetails?.cancellationPolicy?.CancelPenalty?.[0]) {
      const penalty = cartItem.rateDetails.cancellationPolicy.CancelPenalty[0]
      return penalty.Refundable ? 'No deposit required' : 'Deposit required'
    }

    return 'Please contact hotel for cancellation policy'
  }

  const getTravelerInfo = (booking) => {
    if (!booking) return 'Guest User'

    // For experiences
    if (booking.kind === 'experience' || booking.experienceTitle) {
      if (booking.guestInfo) {
        const { firstName, lastName } = booking.guestInfo
        if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim()
      }
      if (booking.customerDetails) {
        const { firstName, lastName } = booking.customerDetails
        if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim()
      }
    }

    // For hotels
    const reservation = booking.generalBackupMetadata?.additionalDetails?.reservationResults?.[0]?.reservation
    if (reservation?.booking?.travelers?.[0]) {
      const traveler = reservation.booking.travelers[0]
      return `${traveler.givenName} ${traveler.surname}`
    }
    return booking.customerDetails?.firstName && booking.customerDetails?.lastName
      ? `${booking.customerDetails.firstName} ${booking.customerDetails.lastName}`
      : booking.generalBackupMetadata?.customerFullName || 'Guest User'
  }

  const getContactInfo = (booking) => {
    if (!booking) return { emails: [], phones: [] }

    const reservation = booking.generalBackupMetadata?.additionalDetails?.reservationResults?.[0]?.reservation
    let emails = reservation?.booking?.contactInfo?.emails || []
    let phones = reservation?.booking?.contactInfo?.phones || []

    if (booking.kind === 'experience' || booking.experienceTitle) {
      if (booking.guestInfo) {
        if (booking.guestInfo.email) emails = [booking.guestInfo.email]
        if (booking.guestInfo.phone) phones = [booking.guestInfo.phone]
      } else if (booking.customerDetails) {
        if (booking.customerDetails.email) emails = [booking.customerDetails.email]
        if (booking.customerDetails.phone) phones = [booking.customerDetails.phone]
      }
    } else if (booking.generalBackupMetadata) {
      if (emails.length === 0) {
        if (booking.customerDetails?.email) emails = [booking.customerDetails.email]
        else if (booking.generalBackupMetadata.customerEmails) emails = booking.generalBackupMetadata.customerEmails
      }
      if (phones.length === 0) {
        if (booking.customerDetails?.phone) phones = [booking.customerDetails.phone]
        else if (booking.generalBackupMetadata.customerTelephones) phones = booking.generalBackupMetadata.customerTelephones
      }
    }

    return { emails, phones }
  }

  const generateProfessionalVoucher = async (booking) => {
    setGeneratingVoucherId(booking._id)

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 15
      const contentWidth = pageWidth - (margin * 2)

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight = 30) => {
        if (yPosition + requiredHeight > pageHeight - 30) {
          doc.addPage()
          yPosition = margin
          return true
        }
        return false
      }

      // Helper function to add section header
      const addSectionHeader = (title, yPos) => {
        doc.setFillColor(15, 23, 42)
        doc.roundedRect(margin, yPos - 5, contentWidth, 8, 2, 2, 'F')
        doc.setFontSize(10)
        doc.setTextColor(255, 255, 255)
        doc.setFont('helvetica', 'bold')
        doc.text(title.toUpperCase(), margin + 8, yPos + 1)
        return yPos + 10
      }

      // Helper function to add two-column layout
      const addTwoColumnText = (leftText, rightText, yPos, leftWidth = contentWidth / 2 - 5) => {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(71, 85, 105)

        let maxHeight = 0

        // Left column
        if (leftText) {
          const leftLines = doc.splitTextToSize(leftText, leftWidth - 5)
          doc.text(leftLines, margin, yPos)
          maxHeight = Math.max(maxHeight, leftLines.length * 4)
        }

        // Right column
        if (rightText) {
          const rightLines = doc.splitTextToSize(rightText, contentWidth / 2 - 5)
          doc.text(rightLines, margin + contentWidth / 2 + 5, yPos)
          maxHeight = Math.max(maxHeight, rightLines.length * 4)
        }

        return yPosition + maxHeight + 4
      }

      // Helper function to get status color and text
      const getStatusInfo = (status) => {
        const statusMap = {
          'confirmed': { color: [34, 197, 94], text: 'CONFIRMED' },
          'cancelled': { color: [239, 68, 68], text: 'CANCELLED' },
          'CANCELLED': { color: [239, 68, 68], text: 'CANCELLED' },
          'pending': { color: [245, 158, 11], text: 'PENDING' }
        }
        return statusMap[status?.toLowerCase()] || { color: [100, 116, 139], text: 'UNKNOWN' }
      }

      const isExperienceBooking = booking.kind === 'experience'

      // Professional Header
      const headerHeight = 50
      doc.setFillColor(15, 23, 42)
      doc.rect(0, 0, pageWidth, headerHeight, 'F')

      // Header Content
      const headerContentLeft = margin + 10
      doc.setFontSize(16)
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.text('BOOKING VOUCHER', headerContentLeft, 20)

      doc.setFontSize(8)
      doc.setTextColor(203, 213, 225)
      doc.setFont('helvetica', 'normal')

      const bookingDateHeader = isExperienceBooking
        ? (booking.createdAt ? formatDate(booking.createdAt) : 'N/A')
        : formatDate(booking.timestamp)

      const headerInfo = [
        `Booking Date: ${bookingDateHeader}`,
        `Voucher Generated: ${new Date().toLocaleDateString()}`
      ]

      headerInfo.forEach((text, index) => {
        doc.text(text, headerContentLeft, 30 + (index * 6))
      })

      // Booking Status Badge in Header
      const statusInfo = getStatusInfo(booking.bookingStatus)
      doc.setFillColor(statusInfo.color[0], statusInfo.color[1], statusInfo.color[2])
      const statusWidth = doc.getTextWidth(statusInfo.text) + 8
      doc.roundedRect(pageWidth - margin - statusWidth - 5, 15, statusWidth, 8, 4, 4, 'F')
      doc.setFontSize(8)
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.text(statusInfo.text, pageWidth - margin - statusWidth - 5 + (statusWidth / 2), 20, { align: 'center' })

      let yPosition = headerHeight + 15

      // Guest Information Section
      checkPageBreak(25)
      yPosition = addSectionHeader('Guest Information', yPosition)

      let travelerName = getTravelerInfo(booking)
      let { emails, phones } = getContactInfo(booking)

      // Override guest/contact info for experience bookings
      if (isExperienceBooking && booking.guestInfo) {
        travelerName = `${booking.guestInfo.firstName || ''} ${booking.guestInfo.lastName || ''}`.trim() || 'Guest'
        emails = booking.guestInfo.email ? [booking.guestInfo.email] : emails
        phones = booking.guestInfo.phone ? [booking.guestInfo.phone] : phones
      }

      const guestInfoLeft = [
        `Full Name: ${travelerName}`,
        `Phone: ${phones?.[0] || 'Not provided'}`,
        `Email: ${emails?.[0] || 'Not provided'}`
      ].join('\n')

      const bookingDateGuest = isExperienceBooking
        ? (booking.createdAt ? formatDate(booking.createdAt) : 'N/A')
        : formatDate(booking.timestamp)

      const referenceLabel = isExperienceBooking ? 'Booking Ref' : 'Confirmation ID'
      const referenceValue = isExperienceBooking
        ? (booking.bookingReference || 'N/A')
        : (booking.confirmationId || 'N/A')

      const guestInfoRight = [
        `Booking Date: ${bookingDateGuest}`,
        `Booking Status: ${booking.bookingStatus ? booking.bookingStatus.toUpperCase() : 'N/A'}`,
        `${referenceLabel}: ${referenceValue}`
      ].join('\n')

      yPosition = addTwoColumnText(guestInfoLeft, guestInfoRight, yPosition) + 5

      // Get the original cart item (hotel bookings)
      const cartItem = booking.bookingData?.originalCartItem
      if (cartItem) {
        checkPageBreak(40)

        // Hotel Header
        const hotelName = booking.generalBackupMetadata?.hotelName || cartItem.title || 'Hotel'
        yPosition = addSectionHeader(`Hotel: ${hotelName}`, yPosition)

        const stayPdf = getHotelStaySourcePresentation(booking)
        const stayPdfLines = [
          `Stay source: ${stayPdf.pillText}`,
          ...(stayPdf.detailText ? [stayPdf.detailText] : [])
        ]

        // Hotel Basic Info
        const hotelInfoLeft = [
          `Hotel: ${hotelName}`,
          ...stayPdfLines,
          `Room Type: ${cartItem.ratePlanName || 'Standard Room'}`,
          `Check-in: ${formatDate(booking.generalBackupMetadata?.checkingDate || cartItem.checkInDate)}`,
          `Check-out: ${formatDate(booking.generalBackupMetadata?.checkoutDate || cartItem.checkOutDate)}`
        ].filter(Boolean).join('\n')

        const nights = cartItem.nights || Math.ceil((new Date(cartItem.checkOutDate) - new Date(cartItem.checkInDate)) / (1000 * 60 * 60 * 24))
        const hotelInfoRight = [
          `Nights: ${nights}`,
          `Max Guests: ${cartItem.maxAdults || 1}`,
          `Bed Type: ${cartItem.bedType?.type || 'Standard Bed'}`,
          `Confirmation: ${booking.confirmationId}`
        ].filter(Boolean).join('\n')

        yPosition = addTwoColumnText(hotelInfoLeft, hotelInfoRight, yPosition) + 5

        // Room Details Section
        checkPageBreak(50)
        yPosition = addSectionHeader('Room Details', yPosition)

        // Room price breakdown (aligned with new markup + Stripe fee persistence)
        const roomInfoLeft = [
          `Rate Plan: ${cartItem.ratePlanName || 'Standard Rate'}`,
          `Board Basis: ${cartItem.boardBasis || 'Room Only'}`,
          `Cancellation Policy: ${cartItem.cancellationPolicy?.[0]?.Refundable ? 'Refundable' : 'Non-refundable'}`
        ].join('\n')

        const {
          currency: currencyCode,
          baseAmount,
          taxAndFees,
          markupAmount,
          totalPayable
        } = getHotelBookingPriceBreakdown(booking)

        const roomInfoRight = [
          `Base Amount: ${formatCurrency(baseAmount, currencyCode)}`,
          ...(taxAndFees > 0 ? [`Tax & Fees: ${formatCurrency(taxAndFees, currencyCode)}`] : []),
          ...(markupAmount > 0 ? [`Markup: ${formatCurrency(markupAmount, currencyCode)}`] : []),
          `Total Payable: ${formatCurrency(totalPayable, currencyCode)}`
        ].join('\n')

        yPosition = addTwoColumnText(roomInfoLeft, roomInfoRight, yPosition) + 5

        // Cancellation Policy Details
        if (cartItem.cancellationPolicy?.[0]) {
          checkPageBreak(20)
          yPosition = addSectionHeader('Cancellation Policy Details', yPosition)

          const policy = cartItem.cancellationPolicy[0]
          let policyText = ''

          if (policy.Deadline) {
            if (policy.Deadline.OffsetUnitMultiplier && policy.Deadline.OffsetTimeUnit) {
              policyText = `${policy.Deadline.OffsetUnitMultiplier} ${policy.Deadline.OffsetTimeUnit} ${policy.Deadline.OffsetDropTime || 'before check-in'}`
            } else if (policy.Deadline.AbsoluteDeadline) {
              policyText = `Until ${formatDate(policy.Deadline.AbsoluteDeadline)}`
            }
          } else {
            policyText = 'Before check-in'
          }

          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)
          doc.text(`Cancellation Deadline: ${policyText}`, margin, yPosition)
          yPosition += 7

          doc.text(`Refundable: ${policy.Refundable ? 'Yes' : 'No'}`, margin, yPosition)
          yPosition += 7

          if (policy.CancelPenaltyAmount) {
            doc.text(`Cancellation Fee: ${formatCurrency(policy.CancelPenaltyAmount, cartItem.currency)}`, margin, yPosition)
            yPosition += 7
          }

          yPosition += 5
        }
      } else if (isExperienceBooking) {
        // Experience booking details
        checkPageBreak(40)

        const experienceTitle = booking.experienceTitle || 'Experience'
        yPosition = addSectionHeader(`Experience: ${experienceTitle}`, yPosition)

        const selectedDateText = booking.selectedDate ? formatDate(booking.selectedDate) : null
        const timeText = booking.selectedTimeSlot
          ? (typeof booking.selectedTimeSlot === 'object'
            ? (booking.selectedTimeSlot.label || booking.selectedTimeSlot.startTime)
            : booking.selectedTimeSlot)
          : null

        const experienceInfoLeft = [
          `Experience: ${experienceTitle}`,
          booking.location ? `Location: ${booking.location}` : null,
          selectedDateText ? `Date: ${selectedDateText}` : null,
          timeText ? `Time: ${timeText}` : null,
          `Participants: ${booking.participantCount || 1}`
        ].filter(Boolean).join('\n')

        const experienceInfoRight = [
          `Reference: ${booking.bookingReference || 'N/A'}`,
          `Payment Status: ${booking.paymentStatus || 'Paid'}`,
          booking.totalPrice ? `Total Paid: ${formatCurrency(booking.totalPrice, booking.currency || 'USD')}` : null,
          booking.difficulty ? `Difficulty: ${booking.difficulty}` : null,
          booking.cancellationPolicy ? `Policy: ${booking.cancellationPolicy}` : null
        ].filter(Boolean).join('\n')

        yPosition = addTwoColumnText(experienceInfoLeft, experienceInfoRight, yPosition) + 5

        // Experience highlights
        if (booking.highlights && booking.highlights.length > 0) {
          checkPageBreak(30)
          yPosition = addSectionHeader('Experience Highlights', yPosition)

          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)

          const highlightsText = booking.highlights.slice(0, 8).map((h, idx) => `• ${h}`).join('\n')
          const lines = doc.splitTextToSize(highlightsText, contentWidth)
          doc.text(lines, margin, yPosition)
          yPosition += lines.length * 4 + 5
        }

        // What's included
        if (booking.included && booking.included.length > 0) {
          checkPageBreak(30)
          yPosition = addSectionHeader("What's Included", yPosition)

          doc.setFontSize(9)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)

          const includedText = booking.included.slice(0, 8).map((i, idx) => `• ${i}`).join('\n')
          const lines = doc.splitTextToSize(includedText, contentWidth)
          doc.text(lines, margin, yPosition)
          yPosition += lines.length * 4 + 5
        }
      }

      // Enhanced Environmental Impact Section (ONLY for hotel bookings)
      if (!isExperienceBooking && cartItem) {
        checkPageBreak(30)
        yPosition = addSectionHeader('Environmental Impact', yPosition)

        const envImpact = getEnvironmentImpact(booking)

        // Environmental metrics in a table format
        const envMetrics = [
          { label: 'Electricity Conservation', value: `${envImpact.electricityValue} kWh` },
          { label: 'Carbon Emission Reduction', value: `${envImpact.carbonEmission} kg CO2` },
          { label: 'Environmental Tier', value: envImpact.tier }
        ]

        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')

        envMetrics.forEach((metric, index) => {
          // Main metric
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(8)
          doc.setTextColor(34, 197, 94)
          doc.text(`${metric.label}:`, margin, yPosition)

          doc.setFont('helvetica', 'bold')
          doc.setFontSize(8)
          doc.setTextColor(15, 23, 42)
          doc.text(metric.value, margin + 70, yPosition)

          yPosition += 6
        })

        yPosition += 3

        // Sustainability message
        const sustainabilityMessage = "Your booking demonstrates commitment to environmental responsibility. By choosing Axonall, you contribute to our global sustainability initiatives and help reduce the tourism industry's ecological footprint."
        const messageLines = doc.splitTextToSize(sustainabilityMessage, contentWidth - 5)
        doc.setFontSize(8)
        doc.setTextColor(71, 85, 105)
        doc.text(messageLines, margin, yPosition)
        yPosition += (messageLines.length * 3.5) + 10
      }

      // Total Price Summary
      checkPageBreak(30)
      yPosition = addSectionHeader('Final Price Summary', yPosition)

      let subtotal, taxes, total, currency, priceData;

      if (isExperienceBooking) {
        // Experience booking price breakdown
        const basePrice = (booking.pricePerPerson || 0) * (booking.participantCount || 1)
        const processingFees = booking.totalPrice ? (booking.totalPrice - basePrice) : 0
        total = booking.totalPrice || 0
        currency = booking.currency || 'USD'

        priceData = [
          { description: 'Experience Price', amount: formatCurrency(basePrice, currency) },
          { description: 'Taxes & Fees', amount: formatCurrency(processingFees, currency) },
          { description: 'GRAND TOTAL', amount: formatCurrency(total, currency), bold: true }
        ]
      } else {
        const pb = getHotelBookingPriceBreakdown(booking)
        subtotal = pb.baseAmount
        taxes = pb.taxAndFees
        total = pb.totalPayable
        currency = pb.currency

        priceData = [
          { description: 'Base Amount', amount: formatCurrency(pb.baseAmount, currency) },
          { description: 'Tax & Fees', amount: formatCurrency(pb.taxAndFees, currency) },
          ...(pb.markupAmount > 0
            ? [{ description: 'Markup', amount: formatCurrency(pb.markupAmount, currency) }]
            : []),
          { description: 'GRAND TOTAL', amount: formatCurrency(total, currency), bold: true }
        ]
      }

      priceData.forEach((item, index) => {
        if (index === priceData.length - 1) {
          doc.setDrawColor(100, 116, 139)
          doc.line(margin, yPosition - 2, margin + contentWidth, yPosition - 2)
          yPosition += 2
        }

        doc.setFontSize(10)
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal')
        doc.setTextColor(item.bold ? 15 : 71, 23, 85, item.bold ? 42 : 105)

        doc.text(item.description, margin, yPosition)
        doc.text(item.amount, margin + contentWidth, yPosition, { align: 'right' })
        yPosition += 7
      })

      yPosition += 10

      // Booking Status Summary
      checkPageBreak(20)
      yPosition = addSectionHeader('Booking Status Summary', yPosition)

      const confirmationLabel = isExperienceBooking ? 'Booking Reference' : 'Confirmation ID'
      const confirmationValue = isExperienceBooking
        ? (booking.bookingReference || 'undefined')
        : (booking.confirmationId || 'undefined')
      const bookingDateValue = isExperienceBooking
        ? (booking.createdAt ? formatDate(booking.createdAt) : 'Invalid Date')
        : formatDate(booking.timestamp)

      const statusSummary = [
        `Overall Booking Status: ${(booking.bookingStatus || 'UNKNOWN').toUpperCase()}`,
        `${confirmationLabel}: ${confirmationValue}`,
        `Booking Date: ${bookingDateValue}`,
        `Voucher Generated: ${new Date().toLocaleDateString()}`
      ]

      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(71, 85, 105)

      statusSummary.forEach((line, index) => {
        if (index === 0) {
          const statusInfo = getStatusInfo(booking.bookingStatus)
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(statusInfo.color[0], statusInfo.color[1], statusInfo.color[2])
          doc.text(line, margin, yPosition)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(71, 85, 105)
        } else {
          doc.text(line, margin, yPosition)
        }
        yPosition += 5
      })

      yPosition += 10

      // Important Instructions
      checkPageBreak(40)
      yPosition = addSectionHeader('Important Instructions', yPosition)

      const instructions = isExperienceBooking
        ? [
          '• Present this voucher and valid government-issued photo ID at the meeting point',
          '• Arrive at least 15 minutes before the scheduled start time',
          '• Wear appropriate clothing and footwear as per experience requirements',
          '• Follow all safety guidelines provided by the experience provider',
          '• Contact Axonall support for any booking modifications or questions',
          '• Keep this voucher accessible throughout the experience',
          '• All payments are processed and managed by Axonall Pvt Ltd.'
        ]
        : [
          '• Present this voucher and valid government-issued photo ID at hotel reception',
          '• Standard check-in: 3:00 PM | Check-out: 11:00 AM (times may vary by hotel)',
          '• Credit card may be required for incidental deposits',
          '• Cancellation policies are specific to each room type',
          '• Contact Axonall support for any booking modifications',
          '• Keep this voucher accessible throughout your stay',
          '• All payments are processed and managed by Axonall Pvt Ltd.'
        ]

      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(71, 85, 105)

      instructions.forEach(instruction => {
        if (checkPageBreak(5)) {
          yPosition += 5
        }
        const lines = doc.splitTextToSize(instruction, contentWidth - 5)
        doc.text(lines, margin + 5, yPosition)
        yPosition += (lines.length * 3.5) + 1
      })

      yPosition += 10

      // Terms & Conditions
      checkPageBreak(25)
      // yPosition = addSectionHeader('Terms & Conditions', yPosition)

      const terms = [
        'This voucher is non-transferable and must be presented upon arrival.',
        'Rates are inclusive of taxes as specified. Additional hotel fees may apply.',
        'Axonall acts as intermediary between guest and hotel provider.',
        'Please review individual room cancellation policies carefully.',
        'Voucher valid only for dates and specifications listed above.',
        'All payments are processed and managed by Axonall Pvt Ltd.'
      ]

      doc.setFontSize(7)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(107, 114, 128)

      // terms.forEach(term => {
      //   if (checkPageBreak(4)) {
      //     yPosition += 5
      //   }
      //   const lines = doc.splitTextToSize(`• ${term}`, contentWidth - 5)
      //   doc.text(lines, margin + 2, yPosition)
      //   yPosition += (lines.length * 3) + 1
      // })

      // Footer on every page
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)

        // Page number
        doc.setFontSize(8)
        doc.setTextColor(100, 116, 139)
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' })

        // Footer background
        doc.setFillColor(15, 23, 42)
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F')

        // Footer text
        doc.setFontSize(7)
        doc.setTextColor(203, 213, 225)
        doc.setFont('helvetica', 'normal')
        doc.text('Axonall Luxury Travel • 24/7 Support: support@axonall.com • +1-800-AXONALL',
          pageWidth / 2, pageHeight - 9, { align: 'center' })
        doc.text('Official Booking Voucher • Unauthorized duplication prohibited • www.axonall.com',
          pageWidth / 2, pageHeight - 4, { align: 'center' })
      }

      // Security watermark on all pages
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(35)
        doc.setTextColor(241, 245, 249)
        doc.setFont('helvetica', 'bold')
        doc.setGState(new doc.GState({ opacity: 0.03 }))
        doc.text('AXONALL', pageWidth / 2, pageHeight / 2, {
          align: 'center',
          angle: 45
        })
      }

      // Save with professional filename (confirmationId for hotel, bookingReference for experience)
      const bookingRef = isExperienceBooking ? booking.bookingReference : booking.confirmationId
      const fileName = `Axonall-Voucher-${bookingRef || 'booking'}-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      setMessage({ type: 'success', text: `Professional voucher for ${bookingRef || 'booking'} downloaded successfully!` })
      toast.success(`Voucher for ${bookingRef || 'booking'} downloaded successfully!`)

    } catch (error) {
      console.error('Error generating PDF:', error)
      setMessage({ type: 'error', text: 'Failed to generate professional voucher. Please try again.' })
      toast.error('Failed to generate voucher')
    } finally {
      setGeneratingVoucherId(null)
    }
  }

  const generateWhiteLabelPDF = async (booking, serviceChargeAmount) => {
    setGeneratingWhiteLabel(true)

    try {
      // Use existing profile state or fetch if not available
      let currentProfile = profile
      if (!currentProfile) {
        const profileResponse = await travelAgentService.getProfile()
        currentProfile = profileResponse.data
        setProfile(currentProfile)
      }
      const profileData = currentProfile
      const agentProfile = profileData // Local reference for PDF generation

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 15
      const contentWidth = pageWidth - (margin * 2)

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight = 30) => {
        if (yPosition + requiredHeight > pageHeight - 30) {
          doc.addPage()
          yPosition = margin
          return true
        }
        return false
      }

      // Helper function to add section header
      const addSectionHeader = (title, yPos) => {
        doc.setFillColor(15, 23, 42)
        doc.roundedRect(margin, yPos - 5, contentWidth, 8, 2, 2, 'F')
        doc.setFontSize(10)
        doc.setTextColor(255, 255, 255)
        doc.setFont('helvetica', 'bold')
        doc.text(title.toUpperCase(), margin + 8, yPos + 1)
        return yPos + 10
      }

      // Helper function to add two-column layout
      const addTwoColumnText = (leftText, rightText, yPos, leftWidth = contentWidth / 2 - 5) => {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(71, 85, 105)

        let maxHeight = 0

        // Left column
        if (leftText) {
          const leftLines = doc.splitTextToSize(leftText, leftWidth - 5)
          doc.text(leftLines, margin, yPos)
          maxHeight = Math.max(maxHeight, leftLines.length * 4)
        }

        // Right column
        if (rightText) {
          const rightLines = doc.splitTextToSize(rightText, contentWidth / 2 - 5)
          doc.text(rightLines, margin + contentWidth / 2 + 5, yPos)
          maxHeight = Math.max(maxHeight, rightLines.length * 4)
        }

        return yPos + maxHeight + 4
      }

      // Helper function to get status color and text
      const getStatusInfo = (status) => {
        const statusMap = {
          'confirmed': { color: [34, 197, 94], text: 'CONFIRMED' },
          'cancelled': { color: [239, 68, 68], text: 'CANCELLED' },
          'CANCELLED': { color: [239, 68, 68], text: 'CANCELLED' },
          'pending': { color: [245, 158, 11], text: 'PENDING' }
        }
        return statusMap[status?.toLowerCase()] || { color: [100, 116, 139], text: 'UNKNOWN' }
      }

      const isExperienceBooking = booking.kind === 'experience'
      const cartItem = booking.bookingData?.originalCartItem

      // Professional Header
      const headerHeight = 50
      doc.setFillColor(15, 23, 42)
      doc.rect(0, 0, pageWidth, headerHeight, 'F')

      // Header Content
      let currentHeaderX = margin + 5

      // Add Logo if exists
      if (agentProfile?.companyLogoUrl) {
        try {
          // Fetch image as base64 to handle CORS issues with S3
          const logoDataUrl = await getImageDataUrl(agentProfile.companyLogoUrl)
          if (logoDataUrl) {
            doc.addImage(logoDataUrl, 'PNG', margin, 10, 30, 30, undefined, 'FAST')
            currentHeaderX = margin + 40
          }
        } catch (e) {
          console.error('Failed to add logo to PDF:', e)
        }
      }

      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(14)
      doc.text(agentProfile?.legalBusinessName || 'BOOKING CONFIRMATION', currentHeaderX, 18)

      doc.setFontSize(8)
      doc.setTextColor(203, 213, 225)
      doc.setFont('helvetica', 'normal')

      const agentDetails = [
        agentProfile?.businessAddress,
        agentProfile?.businessPhoneNumber ? `Tel: ${agentProfile.businessPhoneNumber}` : null,
        agentProfile?.businessEmail ? `Email: ${agentProfile.businessEmail}` : null,
        agentProfile?.websiteUrl ? `Web: ${agentProfile.websiteUrl}` : null
      ].filter(Boolean)

      let currentDetailY = 24
      agentDetails.forEach((text) => {
        const maxDetailWidth = pageWidth - currentHeaderX - margin - 40 // Leave space for status badge
        const lines = doc.splitTextToSize(text, maxDetailWidth)
        doc.text(lines, currentHeaderX, currentDetailY)
        currentDetailY += (lines.length * 4)
      })

      // Booking Status Badge in Header
      const statusInfo = getStatusInfo(booking.bookingStatus || booking.status)
      doc.setFillColor(statusInfo.color[0], statusInfo.color[1], statusInfo.color[2])
      const statusWidth = doc.getTextWidth(statusInfo.text) + 8
      doc.roundedRect(pageWidth - margin - statusWidth - 5, 15, statusWidth, 8, 4, 4, 'F')
      doc.setFontSize(8)
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.text(statusInfo.text, pageWidth - margin - statusWidth - 5 + (statusWidth / 2), 20, { align: 'center' })

      let yPosition = headerHeight + 15

      // Travel Agent/Company Branding Section
      if (agentProfile) {
        checkPageBreak(20)
        yPosition = addSectionHeader('Travel Agent Information', yPosition)

        const agentInfoLeft = [
          `Agent/Company: ${agentProfile?.legalBusinessName || 'Travel Agent'}`,
          agentProfile?.businessAddress ? `Address: ${agentProfile.businessAddress}` : null,
          agentProfile?.businessPhoneNumber ? `Phone: ${agentProfile.businessPhoneNumber}` : null
        ].filter(Boolean).join('\n')

        const agentInfoRight = [
          agentProfile?.businessEmail ? `Email: ${agentProfile.businessEmail}` : null,
          agentProfile?.websiteUrl ? `Website: ${agentProfile.websiteUrl}` : null
        ].filter(Boolean).join('\n')

        yPosition = addTwoColumnText(agentInfoLeft, agentInfoRight, yPosition) + 5
      }

      // Guest Information Section
      checkPageBreak(25)
      yPosition = addSectionHeader('Guest Information', yPosition)

      let travelerName = getTravelerInfo(booking)
      let { emails, phones } = getContactInfo(booking)

      if (isExperienceBooking && booking.guestInfo) {
        travelerName = `${booking.guestInfo.firstName || ''} ${booking.guestInfo.lastName || ''}`.trim() || 'Guest'
        emails = booking.guestInfo.email ? [booking.guestInfo.email] : emails
        phones = booking.guestInfo.phone ? [booking.guestInfo.phone] : phones
      }

      const guestInfoLeft = [
        `Full Name: ${travelerName}`,
        `Phone: ${phones?.[0] || 'Not provided'}`,
        `Email: ${emails?.[0] || 'Not provided'}`
      ].join('\n')

      const bookingDateGuest = isExperienceBooking
        ? (booking.createdAt ? formatDate(booking.createdAt) : 'N/A')
        : formatDate(booking.timestamp)

      const referenceLabel = isExperienceBooking ? 'Booking Reference' : 'Confirmation ID'
      const referenceValue = isExperienceBooking
        ? (booking.bookingReference || 'N/A')
        : (booking.confirmationId || 'N/A')

      const guestInfoRight = [
        `Booking Date: ${bookingDateGuest}`,
        `Status: ${(booking.bookingStatus || booking.status || 'UNKNOWN').toUpperCase()}`,
        `${referenceLabel}: ${referenceValue}`
      ].join('\n')

      yPosition = addTwoColumnText(guestInfoLeft, guestInfoRight, yPosition) + 5

      // Booking Details Section
      if (isExperienceBooking) {
        checkPageBreak(40)
        const experienceTitle = booking.experienceTitle || 'Experience'
        yPosition = addSectionHeader(`Experience: ${experienceTitle}`, yPosition)

        const selectedDateText = booking.selectedDate ? formatDate(booking.selectedDate) : null
        const timeText = booking.selectedTimeSlot
          ? (typeof booking.selectedTimeSlot === 'object'
            ? (booking.selectedTimeSlot.label || booking.selectedTimeSlot.startTime)
            : booking.selectedTimeSlot)
          : null

        const experienceInfoLeft = [
          `Experience: ${experienceTitle}`,
          booking.location ? `Location: ${booking.location}` : null,
          selectedDateText ? `Date: ${selectedDateText}` : null,
          timeText ? `Time: ${timeText}` : null,
          `Participants: ${booking.participantCount || 1}`
        ].filter(Boolean).join('\n')

        const experienceInfoRight = [
          `Reference: ${booking.bookingReference || 'N/A'}`,
          `Payment Status: ${booking.paymentStatus || 'Paid'}`,
          `Price per Person: ${formatCurrency(booking.pricePerPerson || 0, booking.currency || 'USD')}`,
          booking.difficulty ? `Difficulty: ${booking.difficulty}` : null,
          booking.cancellationPolicy ? `Policy: ${booking.cancellationPolicy}` : null
        ].filter(Boolean).join('\n')

        yPosition = addTwoColumnText(experienceInfoLeft, experienceInfoRight, yPosition) + 5
      } else if (cartItem) {
        checkPageBreak(40)
        const hotelName = booking.generalBackupMetadata?.hotelName || cartItem.title || 'Hotel'
        yPosition = addSectionHeader(`Hotel: ${hotelName}`, yPosition)

        const stayPdfWl = getHotelStaySourcePresentation(booking)
        const stayPdfWlLines = [
          `Stay source: ${stayPdfWl.pillText}`,
          ...(stayPdfWl.detailText ? [stayPdfWl.detailText] : [])
        ]

        const hotelInfoLeft = [
          `Hotel: ${hotelName}`,
          ...stayPdfWlLines,
          `Room Type: ${cartItem.ratePlanName || 'Standard Room'}`,
          `Check-in: ${formatDate(booking.generalBackupMetadata?.checkingDate || cartItem.checkInDate)}`,
          `Check-out: ${formatDate(booking.generalBackupMetadata?.checkoutDate || cartItem.checkOutDate)}`
        ].filter(Boolean).join('\n')

        const nights = cartItem.nights || Math.ceil((new Date(cartItem.checkOutDate) - new Date(cartItem.checkInDate)) / (1000 * 60 * 60 * 24))
        const hotelInfoRight = [
          `Nights: ${nights}`,
          `Max Guests: ${cartItem.maxAdults || 1}`,
          `Bed Type: ${cartItem.bedType?.type || 'Standard Bed'}`,
          `Confirmation: ${booking.confirmationId}`
        ].filter(Boolean).join('\n')

        yPosition = addTwoColumnText(hotelInfoLeft, hotelInfoRight, yPosition) + 5
      }

      // Price Breakdown Section
      checkPageBreak(30)
      yPosition = addSectionHeader('Price Breakdown', yPosition)

      const serviceChargeNum = parseFloat(serviceChargeAmount) || 0

      let priceData = []
      if (isExperienceBooking) {
        const basePrice = (booking.pricePerPerson || 0) * (booking.participantCount || 1)
        const otherFees = (booking.totalPrice || 0) - basePrice
        const currency = booking.currency || 'USD'
        const finalAmount = (booking.totalPrice || 0) + serviceChargeNum

        priceData = [
          { description: `Experience Price (${booking.participantCount || 1} Guests)`, amount: formatCurrency(basePrice > 0 ? basePrice : (booking.totalPrice || 0), currency) }
        ]

        if (basePrice > 0 && otherFees > 0) {
          priceData.push({ description: 'Tax & Fees', amount: formatCurrency(otherFees, currency) })
        }

        priceData.push({ description: 'Service Charge', amount: formatCurrency(serviceChargeNum, currency) })
        priceData.push({ description: 'TOTAL DUE', amount: formatCurrency(finalAmount, currency), bold: true })
      } else {
        const pb = getHotelBookingPriceBreakdown(booking)
        const finalAmount = pb.totalPayable + serviceChargeNum

        priceData = [
          { description: 'Base Amount', amount: formatCurrency(pb.baseAmount, pb.currency) },
          { description: 'Tax & Fees', amount: formatCurrency(pb.taxAndFees, pb.currency) },
          ...(pb.markupAmount > 0
            ? [{ description: 'Markup', amount: formatCurrency(pb.markupAmount, pb.currency) }]
            : []),
          { description: 'Booking Total', amount: formatCurrency(pb.totalPayable, pb.currency) },
          { description: 'Service Charge', amount: formatCurrency(serviceChargeNum, pb.currency) },
          { description: 'TOTAL DUE', amount: formatCurrency(finalAmount, pb.currency), bold: true }
        ]
      }

      priceData.forEach((item, index) => {
        if (index === priceData.length - 1) {
          doc.setDrawColor(100, 116, 139)
          doc.line(margin, yPosition - 2, margin + contentWidth, yPosition - 2)
          yPosition += 2
        }

        doc.setFontSize(10)
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal')
        doc.setTextColor(item.bold ? 15 : 71, item.bold ? 23 : 85, item.bold ? 42 : 105)

        doc.text(item.description, margin, yPosition)
        doc.text(item.amount, margin + contentWidth, yPosition, { align: 'right' })
        yPosition += 7
      })

      yPosition += 10

      // Important Information
      checkPageBreak(40)
      yPosition = addSectionHeader('Important Information', yPosition)

      const importantInfo = [
        'Check cancellation policies specific to this booking carefully.',
        'Contact your travel advisor for any modifications or questions.',
        'This document is non-transferable and for the guest named above only.',
        'All payments are processed and managed by Axonall Pvt Ltd.'
      ]

      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(71, 85, 105)

      importantInfo.forEach((info, index) => {
        if (checkPageBreak(5)) {
          yPosition += 3
        }
        const lines = doc.splitTextToSize(`• ${info}`, contentWidth - 5)
        doc.text(lines, margin + 5, yPosition)
        yPosition += (lines.length * 3.5) + 2
      })

      yPosition += 10

      // Footer on every page
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)

        // Page number
        doc.setFontSize(8)
        doc.setTextColor(100, 116, 139)
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' })

        // Footer background
        doc.setFillColor(15, 23, 42)
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F')

        // Footer text
        doc.setFontSize(7)
        doc.setTextColor(203, 213, 225)
        doc.setFont('helvetica', 'normal')
        const agentName = profileData?.legalBusinessName || 'Travel Agent'
        doc.text(`${agentName} • Booking Confirmation`,
          pageWidth / 2, pageHeight - 9, { align: 'center' })
        doc.text('This document is confidential and provided by your travel advisor',
          pageWidth / 2, pageHeight - 4, { align: 'center' })
      }

      // Security watermark on all pages
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setFontSize(35)
        doc.setTextColor(241, 245, 249)
        doc.setFont('helvetica', 'bold')
        doc.setGState(new doc.GState({ opacity: 0.03 }))
        doc.text('WHITE LABEL', pageWidth / 2, pageHeight / 2, {
          align: 'center',
          angle: 45
        })
      }

      // Save with professional filename
      const bookingRef = isExperienceBooking ? booking.bookingReference : booking.confirmationId
      const fileName = `confirmation-${bookingRef || 'booking'}-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      toast.success('White label PDF downloaded successfully!')
      closeWhiteLabelModal()

    } catch (error) {
      console.error('Error generating white label PDF:', error)
      toast.error('Failed to generate white label PDF')
    } finally {
      setGeneratingWhiteLabel(false)
    }
  }

  const getMonthsOptions = () => {
    const months = []
    const currentDate = new Date()

    // Generate options for last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const monthStr = month.toString().padStart(2, '0')
      months.push({
        value: `${year}-${monthStr}`,
        label: `${date.toLocaleString('default', { month: 'long' })} ${year}`
      })
    }

    return months
  }

  const getStatusOptions = () => [
    { value: 'all', label: 'All Statuses' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ]

  const { bookings, pagination } = bookingsData

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50/50">
        {/* Hero */}
        <section className="relative h-96 bg-black overflow-hidden">
          <div className='h-12'></div>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
            style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <p className="text-sm text-slate-300 font-medium tracking-wider uppercase">Your Bookings</p>
              </div>
              <h2 className="text-4xl sm:text-5xl typography-hero-title-on-dark font-bold text-white mb-3">Booking History</h2>
              <p className="text-slate-300 text-lg max-w-2xl mb-6">
                Manage and track all your hotel and experience reservations
              </p>
              <div className="flex items-center space-x-4">
                <span className="text-slate-300 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 text-sm font-medium">
                  {isLoading
                    ? 'Loading bookings...'
                    : `Showing ${bookings.length} of ${pagination.totalCount} bookings${pagination.totalPages > 1 ? ` (Page ${pagination.currentPage} of ${pagination.totalPages})` : ''}`}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pb-12 relative z-20">

          {/* Success/Error Messages */}
          {message.text && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
              <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {message.text}
              </div>
            </div>
          )}

          {/* Filters Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button className="flex items-center px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors duration-200">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </button>

                  {(selectedMonth || selectedStatus !== 'all' || selectedType !== 'all' || selectedHotelStayKind !== 'all' || searchQuery || searchInput) ? (
                    <button
                      onClick={clearAllFilters}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200"
                    >
                      Clear All
                    </button>
                  ) : null}
                </div>

                {/* Search Bar - search runs on button click or Enter only */}
                <div className="relative flex-1 max-w-full flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search by name, email, confirmation ID or booking ref..."
                      value={searchInput}
                      onChange={handleSearchInputChange}
                      onKeyDown={handleSearchKeyDown}
                      className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                    {(searchInput || searchQuery) && (
                      <button
                        type="button"
                        onClick={clearSearchInput}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={applySearch}
                    className="px-4 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 whitespace-nowrap"
                  >
                    Search
                  </button>
                </div>

                {showFilters && (
                  <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                    <div className="flex items-center gap-2">
                      <label htmlFor="month-filter" className="text-sm font-medium text-slate-700 whitespace-nowrap">
                        Month:
                      </label>
                      <select
                        id="month-filter"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">All Months</option>
                        {getMonthsOptions().map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* <div className="flex items-center gap-2">
                  <label htmlFor="status-filter" className="text-sm font-medium text-slate-700 whitespace-nowrap">
                    Status:
                  </label>
                  <select
                    id="status-filter"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {getStatusOptions().map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div> */}

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700 whitespace-nowrap">
                        Type:
                      </span>
                      <div className="inline-flex rounded-lg border border-slate-300 bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => applyTypeFilter('all')}
                          className={`px-3 py-1.5 text-sm font-medium ${selectedType === 'all'
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => applyTypeFilter('hotel')}
                          className={`px-3 py-1.5 text-sm font-medium border-l border-slate-300 ${selectedType === 'hotel'
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          Hotel
                        </button>
                        <button
                          type="button"
                          onClick={() => applyTypeFilter('experience')}
                          className={`px-3 py-1.5 text-sm font-medium border-l border-slate-300 ${selectedType === 'experience'
                            ? 'bg-slate-900 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          Experience
                        </button>
                      </div>
                    </div>

                    {(selectedType === 'hotel' || selectedType === 'all') && (
                      <div className="flex items-center gap-2">
                        <label htmlFor="hotel-stay-kind" className="text-sm font-medium text-slate-700 whitespace-nowrap">
                          Hotels:
                        </label>
                        <select
                          id="hotel-stay-kind"
                          value={selectedHotelStayKind}
                          onChange={(e) => {
                            setSelectedHotelStayKind(e.target.value)
                            setCurrentPage(1)
                          }}
                          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        >
                          <option value="all">All hotel bookings</option>
                          <option value="gds">Catalog (GDS) stays</option>
                          <option value="off_market">Off-market stays</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Search Results Info */}
              {searchQuery && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    Showing {bookings.length} of {pagination.totalCount} bookings matching "{searchQuery}"
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Pagination Controls - Top (hidden while loading) */}
          {!isLoading && pagination.totalPages > 1 && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
              <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                <div className="text-sm text-slate-600">
                  Showing page {pagination.currentPage} of {pagination.totalPages}
                  ({pagination.totalCount} total bookings)
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="flex items-center px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors duration-200"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${pagination.currentPage === pageNum
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="flex items-center px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors duration-200"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 min-h-[280px] bg-white rounded-2xl border border-slate-200 shadow-sm">
                <LoadingSpinner size="xl" />
                <p className="mt-4 text-slate-600 font-medium">Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                    <FileText className="h-12 w-12 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {pagination.totalCount === 0 ? 'No bookings found' : 'No bookings match your filters'}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {pagination.totalCount === 0
                      ? selectedType === 'experience'
                        ? "You haven't made any experience bookings yet. Start by exploring our experiences."
                        : selectedType === 'hotel'
                          ? "You haven't made any hotel bookings yet. Start by exploring our luxury hotels."
                          : "You haven't made any bookings yet. Start by exploring our hotels and experiences."
                      : "Try adjusting your filters to see more results."
                    }
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    {(selectedType === 'all' || selectedType === 'experience') && (
                      <button
                        onClick={() => navigate('/experiences')}
                        className="inline-flex items-center px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors duration-200"
                      >
                        <Compass className="h-5 w-5 mr-2" />
                        Browse Experiences
                      </button>
                    )}
                    {(selectedType === 'all' || selectedType === 'hotel') && (
                      <button
                        onClick={() => navigate('/hotels')}
                        className="inline-flex items-center px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors duration-200"
                      >
                        <Hotel className="h-5 w-5 mr-2" />
                        Browse Hotels
                      </button>
                    )}
                    {selectedType === 'all' && (
                      <button
                        type="button"
                        onClick={() => navigate('/private-jets')}
                        className="inline-flex items-center px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors duration-200"
                      >
                        <Plane className="h-5 w-5 mr-2" />
                        Browse Private Jets
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {bookings.map((booking) => {
                  // Render experience bookings with an elegant, feature-rich card
                  if (booking.kind === 'experience') {
                    const statusKey = (booking.bookingStatus || '').toLowerCase()
                    const isNonRefundable = booking.cancellationPolicy === 'non_refundable'
                    const isCancelled = statusKey === 'cancelled'

                    // Black & white theme: no category colors, use neutral only
                    // Difficulty badge: neutral grays
                    const difficultyConfig = {
                      'easy': { bg: 'bg-gray-100', text: 'text-gray-800', icon: '✓' },
                      'moderate': { bg: 'bg-gray-100', text: 'text-gray-700', icon: '◐' },
                      'challenging': { bg: 'bg-gray-200', text: 'text-gray-900', icon: '▲' },
                      'extreme': { bg: 'bg-gray-200', text: 'text-gray-900', icon: '⚠' }
                    }
                    const difficultyStyle = difficultyConfig[booking.difficulty] || difficultyConfig.moderate

                    // Cover image from experience (same as detail page: isCover or first image)
                    const experienceImages = booking.images || booking.experienceId?.images || []
                    const coverImage = experienceImages.length > 0
                      ? (experienceImages.find(img => img && img.isCover) || experienceImages[0])
                      : null
                    const coverImageUrl = coverImage ? (typeof coverImage === 'string' ? coverImage : coverImage.url) : null

                    return (
                      <div key={booking._id} className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                        {/* Header - black & white theme */}
                        <div className="relative bg-black px-4 py-3 border-b border-gray-800 overflow-hidden">
                          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center flex-wrap gap-3">
                              <div className="inline-flex">
                                {getStatusBadge(statusKey)}
                              </div>
                              <span className="text-xs font-mono text-white bg-gray-800 px-2 py-1 rounded-sm border border-gray-600">
                                📋 {booking.bookingReference}
                              </span>
                              {booking.category && (
                                <span className="hidden sm:inline-flex items-center text-xs font-semibold text-white/90 bg-gray-800 px-2 py-1 rounded-sm border border-gray-600">
                                  {booking.category}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-white/90 bg-gray-800 px-2 py-1 rounded-sm border border-gray-600">
                                🗓️ {booking.createdAt ? formatDate(booking.createdAt) : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Main Content - same layout as hotel card: image left, details right */}
                        <div className="p-4 bg-white">
                          <div className="flex flex-col lg:flex-row gap-3 mb-3">
                            {/* Experience image - same size/placement as hotel room image */}
                            <div className="lg:w-1/3 flex-shrink-0">
                              <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                                {coverImageUrl ? (
                                  <img
                                    src={coverImageUrl}
                                    alt={booking.experienceTitle || 'Experience'}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop'
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-600 text-sm font-medium">No image</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Experience Title & meta - right side like hotel details */}
                            <div className="lg:w-2/3 relative">
                              <div className="absolute -left-6 top-0 bottom-0 w-1.5 bg-black rounded-r-full hidden lg:block"></div>
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h3 className="text-xl font-bold text-gray-900 mb-1 leading-tight">
                                    {booking.experienceTitle}
                                  </h3>
                                  {booking.category && (
                                    <span className="inline-flex items-center px-4 py-1.5 text-xs font-semibold text-white bg-gray-800 rounded-sm border border-gray-600 sm:hidden">
                                      {booking.category}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-sm">
                                {booking.location && (
                                  <div className="flex items-center bg-gray-100 text-gray-700 px-3 py-1.5 rounded-sm border border-gray-200">
                                    <MapPin className="h-4 w-4 mr-1.5 text-gray-500" />
                                    <span className="font-medium">{booking.location}</span>
                                  </div>
                                )}
                                {booking.difficulty && (
                                  <div className={`flex items-center ${difficultyStyle.bg} ${difficultyStyle.text} px-3 py-1.5 rounded-sm border border-gray-300`}>
                                    <span className="text-base mr-1.5">{difficultyStyle.icon}</span>
                                    <span className="font-semibold capitalize">{booking.difficulty}</span>
                                  </div>
                                )}
                                {booking.duration && (booking.duration.hours || booking.duration.days) && (
                                  <div className="flex items-center bg-gray-100 text-gray-700 px-3 py-1.5 rounded-sm border border-gray-200">
                                    <Clock className="h-4 w-4 mr-1.5 text-gray-600" />
                                    <span className="font-semibold">
                                      {booking.duration.days
                                        ? `${booking.duration.days} Day${booking.duration.days > 1 ? 's' : ''}`
                                        : `${booking.duration.hours}h ${booking.duration.minutes || 0}m`}
                                    </span>
                                  </div>
                                )}
                                {booking.transformationArc && (
                                  <div className="flex items-center bg-gray-100 text-gray-800 px-3 py-1.5 rounded-sm border border-gray-200">
                                    <span className="font-semibold">✨ {booking.transformationArc}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Quick Info Stats Row - black & white theme */}
                          <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                            {/* Participants mini stat */}
                            <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <Users className="h-4 w-4 text-gray-600" />
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Guests</p>
                              </div>
                              <p className="text-lg font-bold text-gray-900">{booking.participantCount || 1}</p>
                            </div>

                            {/* Languages mini stat */}
                            {booking.languages && booking.languages.length > 0 && (
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                  </svg>
                                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Language</p>
                                </div>
                                <p className="text-xs font-semibold text-gray-900 truncate">{booking.languages.slice(0, 2).join(', ')}</p>
                              </div>
                            )}

                            {/* Duration mini stat */}
                            {booking.duration && (booking.duration.hours || booking.duration.days) && (
                              <div className="bg-gray-50 p-2 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <Clock className="h-4 w-4 text-gray-600" />
                                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Duration</p>
                                </div>
                                <p className="text-sm font-bold text-gray-900">
                                  {booking.duration.days
                                    ? `${booking.duration.days} Day${booking.duration.days > 1 ? 's' : ''}`
                                    : `${booking.duration.hours}h ${booking.duration.minutes || 0}m`}
                                </p>
                              </div>
                            )}

                            {/* Category mini stat */}
                            {booking.category && (
                              <div className="bg-black p-2 rounded-lg border border-gray-800">
                                <p className="text-xs text-white/80 font-semibold uppercase tracking-wide mb-0.5">Category</p>
                                <p className="text-xs font-bold text-white truncate">{booking.category}</p>
                              </div>
                            )}
                          </div>

                          {/* Guest Information - black & white theme */}
                          {booking.guestInfo && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-2">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <User className="h-4 w-4 text-white" />
                                </div>
                                <h4 className="font-bold text-gray-900 text-sm">Guest Information</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div className="flex items-center bg-white p-2 rounded-lg border border-gray-200">
                                  <div className="p-1.5 bg-gray-100 rounded-lg mr-2">
                                    <User className="h-3.5 w-3.5 text-gray-700" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 font-semibold mb-0.5">Full Name</p>
                                    <p className="text-sm font-bold text-gray-900">{booking.guestInfo.firstName} {booking.guestInfo.lastName}</p>
                                  </div>
                                </div>
                                {booking.guestInfo.email && (
                                  <div className="flex items-center bg-white p-2 rounded-lg border border-gray-200">
                                    <div className="p-1.5 bg-gray-100 rounded-lg mr-2">
                                      <Mail className="h-4 w-4 text-gray-700" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs text-gray-500 font-semibold mb-0.5">Email</p>
                                      <p className="text-sm font-bold text-gray-900 truncate">{booking.guestInfo.email}</p>
                                    </div>
                                  </div>
                                )}
                                {booking.guestInfo.phone && (
                                  <div className="flex items-center bg-white p-2 rounded-lg border border-gray-200 md:col-span-2">
                                    <div className="p-1.5 bg-gray-100 rounded-lg mr-2">
                                      <Phone className="h-4 w-4 text-gray-700" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 font-semibold mb-0.5">Phone</p>
                                      <p className="text-sm font-bold text-gray-900">{booking.guestInfo.phone}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Experience Details Grid - black & white theme */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                            {/* Date & Time Card */}
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-1.5">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <Calendar className="h-4 w-4 text-white" />
                                </div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Scheduled Date</p>
                              </div>
                              <p className="text-base font-bold text-gray-900 mb-0.5">
                                {booking.selectedDate ? formatDate(booking.selectedDate) : booking.selectedDate}
                              </p>
                              {booking.selectedTimeSlot && (
                                <div className="flex items-center mt-1 text-sm text-gray-600 bg-white px-2 py-0.5 rounded border border-gray-200">
                                  <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-600" />
                                  <span className="font-semibold">
                                    {typeof booking.selectedTimeSlot === 'object'
                                      ? booking.selectedTimeSlot.label || booking.selectedTimeSlot.startTime
                                      : booking.selectedTimeSlot}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Participants Card */}
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-1.5">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <Users className="h-4 w-4 text-white" />
                                </div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Participants</p>
                              </div>
                              <p className="text-base font-bold text-gray-900 mb-0.5">
                                {booking.participantCount || 1} {booking.participantCount === 1 ? 'Guest' : 'Guests'}
                              </p>
                              {booking.pricePerPerson && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {formatCurrency(booking.pricePerPerson, booking.currency)} <span className="text-xs text-gray-500">per person</span>
                                </p>
                              )}
                            </div>

                            {/* Payment Card */}
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-1.5">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <CreditCard className="h-4 w-4 text-white" />
                                </div>
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Paid</p>
                              </div>
                              <p className="text-xl font-bold text-gray-900 mb-0.5">
                                {formatCurrency(booking.totalPrice, booking.currency)}
                              </p>
                              <div className="flex items-center mt-1">
                                <div className="flex items-center text-xs font-semibold text-gray-700 bg-gray-200 px-2 py-1 rounded border border-gray-300">
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                  {booking.paymentStatus || 'Paid'}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Highlights Section - black & white theme */}
                          {booking.highlights && booking.highlights.length > 0 && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-2">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <Star className="h-4 w-4 text-white fill-white" />
                                </div>
                                <h4 className="font-bold text-gray-900 text-sm">Experience Highlights</h4>
                              </div>
                              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {booking.highlights.slice(0, 6).map((highlight, idx) => (
                                  <li key={idx} className="flex items-start bg-white p-2 rounded-lg border border-gray-200">
                                    <span className="flex-shrink-0 w-4 h-4 bg-black text-white rounded-full flex items-center justify-center text-[10px] font-bold mr-2 mt-0.5">✓</span>
                                    <span className="text-xs text-gray-700 font-medium leading-snug">{highlight}</span>
                                  </li>
                                ))}
                              </ul>
                              {booking.highlights.length > 6 && (
                                <p className="text-xs text-gray-600 mt-2 text-center font-semibold">
                                  +{booking.highlights.length - 6} more highlights
                                </p>
                              )}
                            </div>
                          )}

                          {/* What's Included - black & white theme */}
                          {booking.included && booking.included.length > 0 && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center mb-2">
                                <div className="p-1.5 bg-black rounded-lg mr-2">
                                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <h4 className="font-bold text-gray-900 text-sm">What's Included</h4>
                              </div>
                              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {booking.included.slice(0, 6).map((item, idx) => (
                                  <li key={idx} className="flex items-start bg-white p-2 rounded-lg border border-gray-200">
                                    <span className="flex-shrink-0 w-4 h-4 bg-black text-white rounded-full flex items-center justify-center text-[10px] mr-2 mt-0.5">✓</span>
                                    <span className="text-xs text-gray-700 font-medium leading-snug">{item}</span>
                                  </li>
                                ))}
                              </ul>
                              {booking.included.length > 6 && (
                                <p className="text-xs text-gray-600 mt-2 text-center font-semibold">
                                  +{booking.included.length - 6} more included items
                                </p>
                              )}
                            </div>
                          )}

                          {/* Price Breakdown - black & white theme */}
                          <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center mb-2">
                              <div className="p-1.5 bg-black rounded-lg mr-2">
                                <DollarSign className="h-4 w-4 text-white" />
                              </div>
                              <h4 className="font-bold text-gray-900 text-sm">Payment Summary</h4>
                            </div>

                            <div className="bg-white rounded-lg p-3 border border-gray-200 space-y-2">
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 font-medium">
                                  Base Price <span className="text-xs text-gray-400">({booking.participantCount || 1} × {formatCurrency(booking.pricePerPerson, booking.currency)})</span>
                                </span>
                                <span className="font-bold text-gray-900">{formatCurrency((booking.pricePerPerson || 0) * (booking.participantCount || 1), booking.currency)}</span>
                              </div>

                              {booking.processingFees && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="text-gray-600 font-medium">Processing Fees</span>
                                  <span className="font-bold text-gray-900">{formatCurrency(booking.totalPrice - (booking.pricePerPerson * booking.participantCount), booking.currency)}</span>
                                </div>
                              )}

                              <div className="pt-2 border-t-2 border-gray-300">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-bold text-gray-900 uppercase tracking-wide">Total Paid</span>
                                  <span className="text-xl font-bold text-gray-900">{formatCurrency(booking.totalPrice, booking.currency)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Cancellation Policy - hardcoded non refundable (no API mapping) */}
                          <div className="mb-3 p-3 rounded-lg border-t-4 bg-red-50/50 border-red-200 border-t-red-500">
                            <div className="flex items-start">
                              <div className="p-1.5 rounded-lg mr-2 bg-red-600">
                                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold mb-1 text-sm text-red-900">
                                  Cancellation Policy
                                </h4>
                                <div className="text-xs font-semibold text-red-700">
                                  Non refundable
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons - black & white theme */}
                          <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-gray-200">
                            {/* Download Voucher Button - black & white theme */}
                            <button
                              onClick={() => generateProfessionalVoucher(booking)}
                              disabled={generatingVoucherId === booking._id}
                              className="inline-flex items-center px-4 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-black uppercase text-xs tracking-wider"
                            >
                              <div className="flex items-center">
                                {generatingVoucherId === booking._id ? (
                                  <>
                                    <LoadingSpinner size="sm" className="mr-2" />
                                    <span>Generating PDF...</span>
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-5 w-5 mr-2.5" />
                                    <span>Download Confirmation</span>
                                  </>
                                )}
                              </div>
                            </button>

                            {/* Download White Label Button */}
                            <button
                              onClick={() => openWhiteLabelModal(booking)}
                              className="inline-flex items-center px-4 py-2 bg-white text-black font-semibold rounded-lg hover:bg-gray-50 border-2 border-black uppercase text-xs tracking-wider"
                            >
                              <FileText className="h-5 w-5 mr-2.5" />
                              <span>Download White Label</span>
                            </button>

                            {/* Experience Cancel Button - commented out */}
                            {/* {!isCancelled && !isNonRefundable && statusKey === 'confirmed' && (
                          <button
                            onClick={() => openCancelModal(booking, booking.bookingReference)}
                            disabled={cancellingBookingId === booking.bookingReference}
                            className="inline-flex items-center px-6 py-3 bg-transparent text-gray-900 font-semibold rounded-lg hover:bg-gray-100 border-2 border-gray-800 disabled:opacity-50 disabled:cursor-not-allowed uppercase text-xs tracking-wider"
                          >
                            {cancellingBookingId === booking.bookingReference ? (
                              <>
                                <LoadingSpinner size="sm" className="mr-2" />
                                <span>Cancelling...</span>
                              </>
                            ) : (
                              <>
                                  <X className="h-5 w-5 mr-2.5" />
                                  <span>Cancel Booking</span>
                              </>
                            )}
                          </button>
                        )} */}

                            {/* Non-Refundable Badge - black & white theme */}
                            {isNonRefundable && !isCancelled && (
                              <div className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-600 font-semibold rounded-lg cursor-not-allowed border-2 border-gray-300 uppercase text-xs tracking-wider">
                                <X className="h-5 w-5 mr-2 opacity-50" />
                                <span>Non-Refundable</span>
                              </div>
                            )}

                            {/* Cancelled Badge */}
                            {isCancelled && (
                              <div className="inline-flex items-center px-6 py-3 bg-red-50 text-red-700 font-semibold rounded-lg border-2 border-red-200 uppercase text-xs tracking-wider">
                                <XCircle className="h-5 w-5 mr-2" />
                                <span>Booking Cancelled</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  const cartItems = booking.generalBackupMetadata?.additionalDetails?.cartItems || []
                  const travelerName = getTravelerInfo(booking)
                  const { emails, phones } = getContactInfo(booking)
                  const totalRooms = cartItems.length

                  return (
                    <div key={booking._id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      {/* Booking Header */}
                      <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center space-x-3">
                            {getStatusBadge(booking.bookingStatus)}
                            {/* <span className="text-lg font-semibold text-slate-900">
                          Confirmation: {booking.confirmationId}
                        </span> */}
                          </div>
                          <div className="flex items-center space-x-3 mt-3 sm:mt-0">
                            <span className="text-sm text-slate-500">
                              Booked on {formatDate(booking.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Booking Content */}
                      <div className="p-6">
                        {/* Guest & Contact Information */}
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                            <User className="h-4 w-4 mr-2" />
                            Guest Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center text-slate-600">
                              <User className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span>{travelerName}</span>
                            </div>
                            {emails?.[0] && (
                              <div className="flex items-center text-slate-600">
                                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                                <span>{emails[0]}</span>
                              </div>
                            )}
                            {phones?.[0] && (
                              <div className="flex items-center text-slate-600">
                                <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                                <span>{phones[0]}</span>
                              </div>
                            )}
                            {booking.customerDetails?.address && (
                              <div className="flex items-center text-slate-600">
                                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                                <span>{booking.customerDetails.address}, {booking.customerDetails.city}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Hotel Overview */}
                        <div className="mb-6">
                          <h3 className="text-2xl font-semibold text-slate-900 mb-2">
                            {booking.generalBackupMetadata.hotelName}
                          </h3>
                          {(() => {
                            const stay = getHotelStaySourcePresentation(booking)
                            return (
                              <div className="flex flex-wrap items-center gap-2 mb-4">
                                <span
                                  className={`inline-flex px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${
                                    stay.variant === 'offMarket'
                                      ? 'bg-amber-50 text-amber-900 border-amber-200'
                                      : 'bg-slate-100 text-slate-700 border-slate-200'
                                  }`}
                                >
                                  {stay.variant === 'offMarket' ? 'Off-market' : 'GDS hotel'}
                                </span>
                                {stay.detailText ? (
                                  <span className="text-xs text-slate-600 font-medium">{stay.detailText}</span>
                                ) : null}
                              </div>
                            )
                          })()}
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                            <Calendar className="h-4 w-4 flex-shrink-0" />
                            <span>{formatDate(booking.generalBackupMetadata.checkingDate)} – {formatDate(booking.generalBackupMetadata.checkoutDate)}</span>
                          </div>
                        </div>

                        {/* Room Details - Each room in separate card */}
                        <div className="space-y-4 mb-6">

                          <div
                            key={booking._id || booking.confirmationId || booking.bookingKey || `hotel-card-${booking.timestamp}`}
                            className="border border-slate-200 rounded-xl overflow-hidden"
                          >
                            {/* Room Header */}
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                              <div className="flex items-center justify-between">
                                {/* <h4 className="font-semibold text-slate-900 flex items-center">
                                  <Bed className="h-4 w-4 mr-2" />
                                  Room {index + 1} - {cartItem.ratePlanName}
                                </h4> */}
                                <div className="flex flex-col gap-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-lg font-bold text-slate-900">
                                      Confirmation ID : {booking.confirmationId || 'N/A'}
                                    </span>
                                    {/* {booking.bookingReferenceValue && (
                                      <span className="text-sm font-mono text-slate-700 bg-slate-100 px-2 py-1 rounded border border-slate-200">
                                        Ref: {booking.bookingReferenceValue}
                                      </span>
                                    )} */}
                                    {(() => {
                                      const stay = getHotelStaySourcePresentation(booking)
                                      return (
                                        <span
                                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
                                            stay.variant === 'offMarket'
                                              ? 'bg-amber-50 text-amber-900 border-amber-200'
                                              : 'bg-slate-100 text-slate-600 border-slate-200'
                                          }`}
                                        >
                                          {stay.pillText}
                                        </span>
                                      )
                                    })()}
                                    {(() => {
                                      const timing = getHotelStripeTiming(booking)
                                      return (
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${timing.badgeClass}`}>
                                          {timing.label}
                                        </span>
                                      )
                                    })()}
                                  </div>

                                  {(() => {
                                    const timing = getHotelStripeTiming(booking)
                                    const amountText =
                                      timing.amount != null
                                        ? formatCurrency(Number(timing.amount || 0), timing.currency)
                                        : null
                                    const dueText = timing.dueAt ? formatDate(timing.dueAt) : null
                                    return (
                                      <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                                        {amountText && (
                                          <span>
                                            <span className="font-medium">Amount:</span> {amountText}
                                          </span>
                                        )}
                                        {dueText && (
                                          <span>
                                            <span className="font-medium">Scheduled date:</span> {dueText}
                                          </span>
                                        )}
                                        {timing.status && (
                                          <span>
                                            <span className="font-medium">Status:</span> {String(timing.status)}
                                          </span>
                                        )}
                                        {timing.paymentIntentId && (
                                          <span className="truncate max-w-[320px]">
                                            <span className="font-medium">Stripe PI:</span> {timing.paymentIntentId}
                                          </span>
                                        )}
                                      </div>
                                    )
                                  })()}
                                </div>
                              </div>
                            </div>

                            {/* Room Details */}
                            <div className="p-4">
                              <div className="flex flex-col lg:flex-row gap-4">
                                {/* Room Image */}
                                <div className="lg:w-1/3">
                                  <div className="aspect-video bg-slate-200 rounded-lg overflow-hidden">
                                    <img
                                      src={booking.bookingData.originalCartItem.image}
                                      alt={booking.bookingData.originalCartItem.title}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/300x200?text=Hotel+Image'
                                      }}
                                    />
                                  </div>
                                </div>

                                {/* Room Information */}
                                <div className="lg:w-2/3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h5 className="text-xs font-medium text-slate-500 uppercase mb-2">Accommodation Details</h5>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex items-center text-slate-600">
                                          <Bed className="h-4 w-4 mr-2 flex-shrink-0 text-slate-400" />
                                          <span>{booking.bookingData.originalCartItem.bedType?.type || 'Standard Bed'}</span>
                                        </div>
                                        <div className="flex items-center text-slate-600">
                                          <Users className="h-4 w-4 mr-2 flex-shrink-0 text-slate-400" />
                                          <span>Max {booking.bookingData.originalCartItem.maxAdults || 1} {booking.bookingData.originalCartItem.maxAdults === 1 ? 'Adult' : 'Adults'}</span>
                                        </div>
                                        <div className="flex items-center text-slate-600">
                                          <Clock className="h-4 w-4 mr-2 flex-shrink-0 text-slate-400" />
                                          <span>{booking.bookingData.originalCartItem.nights} {booking.bookingData.originalCartItem.nights === 1 ? 'Night' : 'Nights'}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div>
                                      <h5 className="text-xs font-medium text-slate-500 uppercase mb-2">Price Breakdown</h5>
                                      <HotelBookingPriceBreakdown
                                        booking={booking}
                                        formatCurrency={formatCurrency}
                                        variant="detailed"
                                      />
                                    </div>
                                  </div>

                                  {/* Room Policies */}
                                  <div className="mt-4 pt-4 border-t border-slate-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                      {booking.bookingData.originalCartItem.cancellationPolicy[0].Refundable && (
                                        <div>
                                          <span className="font-medium text-slate-700">Cancellation Deadline:</span>
                                          <div className="text-slate-600 mt-1">
                                            {booking.bookingData.originalCartItem.cancellationPolicy?.map((policy, index) => (
                                              <p key={index}>
                                                {policy.Deadline ? (
                                                  <>
                                                    {policy.Deadline.OffsetUnitMultiplier} {policy.Deadline.OffsetTimeUnit}
                                                    {policy.Deadline.OffsetDropTime
                                                      ? policy.Deadline.OffsetDropTime.replace(/([A-Z])/g, ' $1').trim()
                                                      : policy.Deadline.AbsoluteDeadline
                                                        ? new Date(policy.Deadline.AbsoluteDeadline).toLocaleString('en-US', {
                                                          year: 'numeric',
                                                          month: 'short',
                                                          day: 'numeric',
                                                          hour: '2-digit',
                                                          minute: '2-digit',
                                                          hour12: true
                                                        })
                                                        : 'Before Checkin'
                                                    }
                                                  </>
                                                ) : (
                                                  'Before Checkin'
                                                )}
                                              </p>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Room Policies */}
                                  <div className="mt-4 pt-4 border-t border-slate-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                      {booking.bookingStatus === 'CANCELLED' && (

                                        <div>
                                          <div className="flex justify-between text-slate-600">
                                            <span>Cancel Reason :</span>
                                            <span className="font-medium">{booking.cancellationReason || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between text-slate-600">
                                            <span>Cancelation Date :</span>
                                            <span className="font-medium">{formatDate(booking.cancelledAt) || 'N/A'}</span>
                                          </div>
                                        </div>


                                      )}
                                    </div>
                                  </div>

                                  {booking.bookingStatus === 'confirmed' && booking.bookingData.originalCartItem.cancellationPolicy[0].Refundable && (() => {
                                    // Check if cancellation is still allowed based on deadline
                                    const canCancel = booking.bookingData.originalCartItem.cancellationPolicy?.some((policy) => {
                                      if (!policy.Deadline?.OffsetUnitMultiplier || !policy.Deadline?.OffsetTimeUnit) {
                                        return true; // If no deadline specified, allow cancellation
                                      }

                                      const checkInDate = new Date(booking.generalBackupMetadata?.checkingDate);
                                      const currentDate = new Date();

                                      // Calculate the deadline date
                                      const offsetMultiplier = policy.Deadline.OffsetUnitMultiplier;
                                      const offsetTimeUnit = policy.Deadline.OffsetTimeUnit.toLowerCase();

                                      let deadlineDate = new Date(checkInDate);

                                      // Subtract the offset based on time unit
                                      if (offsetTimeUnit === 'day') {
                                        deadlineDate.setDate(checkInDate.getDate() - offsetMultiplier);
                                      } else if (offsetTimeUnit === 'month') {
                                        deadlineDate.setMonth(checkInDate.getMonth() - offsetMultiplier);
                                      } else if (offsetTimeUnit === 'hour') {
                                        deadlineDate.setHours(checkInDate.getHours() - offsetMultiplier);
                                      }

                                      // Check if current date is before the deadline
                                      return currentDate < deadlineDate;
                                    });

                                    return canCancel ? (
                                      <button
                                        onClick={() => openCancelModal(booking, booking.confirmationId)}
                                        className="inline-flex items-center px-6 py-3 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 mt-6"
                                      >
                                        {cancellingBookingId === booking.confirmationId ? (
                                          <>
                                            <LoadingSpinner size="sm" className="mr-2" />
                                            Cancelling...
                                          </>
                                        ) : (
                                          <>
                                            <X className="h-5 w-5 mr-2" />
                                            Cancel Booking
                                          </>
                                        )}
                                      </button>
                                    ) : (
                                      <div className="mt-6 p-3 bg-slate-100 rounded-lg border border-slate-200">
                                        <p className="text-sm text-slate-600 text-center">
                                          Cancellation deadline has passed
                                        </p>
                                      </div>
                                    );
                                  })()}



                                  {!booking.bookingData.originalCartItem.cancellationPolicy[0].Refundable && (
                                    <p className="text-slate-600 mt-1">No cancellation available for this</p>
                                  )}

                                  {booking.bookingStatus === 'CANCELLED' && (
                                    <button
                                      disabled
                                      className="inline-flex items-center px-6 py-3 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 mt-6"
                                    >
                                      <X className="h-5 w-5 mr-2" />
                                      Canceled
                                    </button>
                                  )}

                                </div>
                              </div>
                            </div>
                          </div>

                        </div>

                        {/* Hotel Contact Information */}
                        {cartItems[0]?.hotelDetails && (
                          <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                              <MapPin className="h-4 w-4 mr-2" />
                              Hotel Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                              <div>
                                <span className="font-medium text-slate-700">Address:</span>
                                <p className="mt-1">
                                  {cartItems[0].hotelDetails.address?.AddressLine1}<br />
                                  {cartItems[0].hotelDetails.address?.CityName?.value}, {cartItems[0].hotelDetails.address?.PostalCode}<br />
                                  {cartItems[0].hotelDetails.address?.CountryName?.value}
                                </p>
                              </div>
                              <div>
                                <span className="font-medium text-slate-700">Contact:</span>
                                <p className="mt-1">
                                  Phone: {cartItems[0].hotelDetails.contact?.Phone || 'Not provided'}<br />
                                  Chain: {cartItems[0].hotelDetails.chainName || 'Independent'}
                                  {cartItems[0].hotelDetails.rating && (
                                    <>
                                      <br />
                                      <span className="inline-flex items-center mt-1">
                                        <Star className="h-3 w-3 text-yellow-400 fill-yellow-400 mr-1" />
                                        {cartItems[0].hotelDetails.rating} Star Hotel
                                      </span>
                                    </>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Total Price Summary */}
                        <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-900">Total Booking Amount</h4>
                          </div>
                          <HotelBookingPriceBreakdown
                            booking={booking}
                            formatCurrency={formatCurrency}
                            variant="compact"
                          />
                        </div>

                        {/* Environment Impact Section - Add this after the Total Price Summary */}
                        <div className="mt-6 p-2 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            Environmental Impact
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-2 bg-white rounded-lg border border-green-100">
                              <div className="text-xl font-bold text-green-700">
                                {Number(getEnvironmentImpact(booking).electricityValue || 0).toFixed(2)}kWh
                              </div>
                              <div className="text-sm text-green-600 mt-1">Electricity Saved</div>
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg border border-green-100">
                              <div className="text-xl font-bold text-green-700">
                                {Number(getEnvironmentImpact(booking).carbonEmission || 0).toFixed(2)}kg
                              </div>
                              <div className="text-sm text-green-600 mt-1">CO₂ Reduction</div>
                            </div>
                            <div className="text-center p-2 bg-white rounded-lg border border-green-100">
                              <div className={`text-lg font-bold ${getEnvironmentImpact(booking).tier === 'Low' ? 'text-green-600' :
                                getEnvironmentImpact(booking).tier === 'Medium' ? 'text-yellow-600' :
                                  getEnvironmentImpact(booking).tier === 'High' ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                {getEnvironmentImpact(booking).tier} Impact
                              </div>
                              <div className="text-sm text-green-600 mt-1">Environmental Tier</div>
                            </div>
                          </div>
                          {/* <p className="text-xs text-green-600 mt-3 text-center">
                        Thank you for choosing eco-friendly travel with Axonall
                      </p> */}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-200">
                          <button
                            onClick={() => generateProfessionalVoucher(booking)}
                            disabled={generatingVoucherId === booking._id}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                          >
                            {generatingVoucherId === booking._id ? (
                              <>
                                <LoadingSpinner size="sm" className="mr-2" />
                                Generating PDF...
                              </>
                            ) : (
                              <>
                                <Download className="h-5 w-5 mr-2" />
                                Download Detail Document
                              </>
                            )}
                          </button>

                          <button
                            onClick={() => openWhiteLabelModal(booking)}
                            className="inline-flex items-center px-6 py-3 bg-white text-slate-900 font-medium rounded-lg border-2 border-slate-900 hover:bg-slate-50 transition-all duration-200"
                          >
                            <FileText className="h-5 w-5 mr-2" />
                            Download White Label
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </main>

          {/* Pagination Controls - Bottom (hidden while loading) */}
          {!isLoading && pagination.totalPages > 1 && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                <div className="text-sm text-slate-600">
                  Showing page {pagination.currentPage} of {pagination.totalPages}
                  ({pagination.totalCount} total bookings)
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="flex items-center px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors duration-200"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${pagination.currentPage === pageNum
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="flex items-center px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors duration-200"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Cancellation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Cancel Booking</h3>
                  <button
                    onClick={closeCancelModal}
                    className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-4 p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-700 font-medium">
                    {selectedBooking?.kind === 'experience' ? 'Booking reference' : 'Confirmation ID'}: {selectedConfirmationForCancel}
                  </p>
                  {selectedBooking?.kind === 'experience' ? (
                    <p className="text-sm text-red-600 mt-1">
                      Experience: {selectedBooking?.experienceId?.title || selectedBooking?.experienceTitle || 'N/A'}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-red-600 mt-1">
                        Hotel: {selectedBooking?.generalBackupMetadata?.hotelName}
                      </p>
                      {(() => {
                        const s = getHotelStaySourcePresentation(selectedBooking)
                        return (
                          <p className="text-xs text-red-800/90 mt-1.5 font-semibold uppercase tracking-wide">
                            {s.pillText}
                            {s.detailText ? (
                              <span className="ml-1.5 font-medium normal-case tracking-normal text-red-700">
                                · {s.detailText}
                              </span>
                            ) : null}
                          </p>
                        )
                      })()}
                      <p className="text-sm text-red-600 mt-1">
                        Total Rooms: {selectedBooking?.generalBackupMetadata?.additionalDetails?.cartItems?.length || 1}
                      </p>
                    </>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="cancellation-reason" className="block text-sm font-medium text-slate-700 mb-2">
                    Re-Type {selectedBooking?.kind === 'experience' ? 'Booking Reference' : 'Confirmation ID'} to cancel booking *
                  </label>
                  <input
                    type="text"
                    id="cancellation-reason"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder={selectedBooking?.kind === 'experience' ? 'Enter booking reference' : 'Enter confirmation ID'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Type <span className="font-mono font-semibold">{selectedConfirmationForCancel}</span> to confirm cancellation
                  </p>
                </div>

                <div className="mb-6">
                  <label htmlFor="cancellation-reason-two" className="block text-sm font-medium text-slate-700 mb-2">
                    Type the reason for the cancellation *
                  </label>
                  <input
                    type="text"
                    id="cancellation-reason-two"
                    value={cancellationReasontwo}
                    onChange={(e) => setCancellationReasontwo(e.target.value)}
                    placeholder="Enter cancellation reason"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Required
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={closeCancelModal}
                    disabled={cancellingBookingId}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCancelBooking}
                    disabled={!cancellationReason.trim() || !cancellationReasontwo.trim() || cancellingBookingId}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {cancellingBookingId ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2 inline"></div>
                        Cancelling...
                      </>
                    ) : (
                      'Confirm Cancellation'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Progress Loader */}
        {showCancellationLoader && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/75 backdrop-blur-sm transition-all duration-300">
            <div className="text-center max-w-sm mx-4">
              {/* Animated checkmark circle */}
              <div className="relative w-24 h-24 mx-auto mb-8">
                {/* Outer circle */}
                <div className="absolute inset-0 border-2 border-slate-200 rounded-full"></div>

                {/* Progress circle that fills based on progress */}
                <div
                  className="absolute inset-0 border-2 border-transparent border-t-red-600 rounded-full animate-spin"
                  style={{
                    borderTopColor: cancellationProgress >= 20 ? '#dc2626' : 'transparent',
                    borderRightColor: cancellationProgress >= 40 ? '#dc2626' : 'transparent',
                    borderBottomColor: cancellationProgress >= 60 ? '#dc2626' : 'transparent',
                    borderLeftColor: cancellationProgress >= 80 ? '#dc2626' : 'transparent',
                  }}
                ></div>

                {/* Center checkmark - appears when complete */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-full flex items-center justify-center">
                    {cancellationProgress === 100 ? (
                      <XCircle className="w-8 h-8 text-red-600 animate-pulse" />
                    ) : (
                      <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-semibold text-slate-900 mb-3">
                {cancellationProgress === 100 ? 'Cancellation Complete' : 'Cancelling Your Booking'}
              </h3>

              {/* Progress percentage */}
              <div className="mb-4">
                <span className="text-3xl font-bold text-red-600">{Math.round(cancellationProgress)}%</span>
                <span className="text-sm text-slate-500 ml-2">complete</span>
              </div>

              {/* Message */}
              <p className="text-slate-600 mb-8 leading-relaxed">
                {cancellationProgress === 100
                  ? 'Your booking has been cancelled successfully.'
                  : cancellationSteps[cancellationCurrentStep] || cancellationMessage}
              </p>

              {/* Progress bar */}
              <div className="w-full bg-slate-100 rounded-full h-2 mb-8 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${cancellationProgress}%` }}
                ></div>
              </div>

              {/* Progress indicators */}
              <div className="relative h-8 mb-8">
                {cancellationSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`absolute top-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${index <= cancellationCurrentStep
                      ? 'bg-red-600 text-white scale-110 shadow-lg'
                      : 'bg-slate-200 text-slate-400'
                      }`}
                    style={{
                      left: `${(index / (cancellationSteps.length - 1)) * 100}%`,
                      transform: `translateX(-50%) ${index <= cancellationCurrentStep ? 'scale(110%)' : ''}`
                    }}
                  >
                    {index + 1}
                  </div>
                ))}
                {/* Connecting line */}
                <div className="absolute top-3 left-0 right-0 h-0.5 bg-slate-200 -z-10">
                  <div
                    className="h-full bg-red-600 transition-all duration-500 ease-out"
                    style={{ width: `${(cancellationCurrentStep / (cancellationSteps.length - 1)) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Step descriptions */}
              <div className="grid grid-cols-2 gap-3 text-xs mb-8">
                {cancellationSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded-lg transition-all duration-300 ${index === cancellationCurrentStep
                      ? 'bg-red-50 border border-red-100'
                      : 'text-slate-500'
                      } ${index <= cancellationCurrentStep ? 'font-medium' : ''}`}
                  >
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${index === cancellationCurrentStep
                        ? 'bg-red-600 animate-pulse'
                        : index < cancellationCurrentStep
                          ? 'bg-red-400'
                          : 'bg-slate-300'
                        }`}></div>
                      <span className={index === cancellationCurrentStep ? 'text-red-700' : ''}>
                        {step}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Note */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  {cancellationProgress === 100
                    ? 'Your booking has been cancelled. You will receive a confirmation email shortly.'
                    : 'This usually takes 10-15 seconds. Please don\'t refresh the page.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* White Label Modal */}
        {showWhiteLabelModal && selectedBookingForWhiteLabel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-900">Generate Booking Voucher</h3>
                  <button
                    onClick={closeWhiteLabelModal}
                    className="text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left side - Form */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Service Charge ($)
                      </label>
                      <input
                        type="number"
                        value={serviceCharge}
                        onChange={(e) => setServiceCharge(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-medium text-slate-900 mb-2">Booking Summary</h4>
                      <div className="space-y-1 text-sm text-slate-600">
                        <p>Reference: {selectedBookingForWhiteLabel.bookingReference || selectedBookingForWhiteLabel.confirmationId}</p>
                        <p>{selectedBookingForWhiteLabel.kind === 'experience' ? 'Experience' : 'Hotel'}: {selectedBookingForWhiteLabel.kind === 'experience' ? selectedBookingForWhiteLabel.experienceTitle : (selectedBookingForWhiteLabel.generalBackupMetadata?.hotelName || selectedBookingForWhiteLabel.bookingData?.originalCartItem?.title || 'N/A')}</p>
                        {selectedBookingForWhiteLabel.kind !== 'experience' && (() => {
                          const s = getHotelStaySourcePresentation(selectedBookingForWhiteLabel)
                          return (
                            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                              {s.pillText}
                              {s.detailText ? (
                                <span className="ml-1.5 font-medium normal-case tracking-normal text-slate-600">
                                  · {s.detailText}
                                </span>
                              ) : null}
                            </p>
                          )
                        })()}
                        <p>Guest: {getTravelerInfo(selectedBookingForWhiteLabel)}</p>
                        {selectedBookingForWhiteLabel.kind === 'experience' ? (
                          <>
                            <p>Base Amount: {formatCurrency(selectedBookingForWhiteLabel.totalPrice || 0, selectedBookingForWhiteLabel.currency || 'USD')}</p>
                            <p>Service Charge: {formatCurrency(parseFloat(serviceCharge || 0), selectedBookingForWhiteLabel.currency || 'USD')}</p>
                            <p className="font-medium text-slate-900">
                              Final Amount: {formatCurrency((selectedBookingForWhiteLabel.totalPrice || 0) + parseFloat(serviceCharge || 0), selectedBookingForWhiteLabel.currency || 'USD')}
                            </p>
                          </>
                        ) : (
                          (() => {
                            const pb = getHotelBookingPriceBreakdown(selectedBookingForWhiteLabel)
                            return (
                              <>
                                <p>Base Amount: {formatCurrency(pb.baseAmount, pb.currency)}</p>
                                <p>Tax &amp; Fees: {formatCurrency(pb.taxAndFees, pb.currency)}</p>
                                {pb.markupAmount > 0 && <p>Markup: {formatCurrency(pb.markupAmount, pb.currency)}</p>}
                                <p>Booking Total: {formatCurrency(pb.totalPayable, pb.currency)}</p>
                                <p>Service Charge: {formatCurrency(parseFloat(serviceCharge || 0), pb.currency)}</p>
                                <p className="font-medium text-slate-900">
                                  Final Amount: {formatCurrency(pb.totalPayable + parseFloat(serviceCharge || 0), pb.currency)}
                                </p>
                              </>
                            )
                          })()
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => generateWhiteLabelPDF(selectedBookingForWhiteLabel, serviceCharge)}
                      disabled={generatingWhiteLabel}
                      className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
                    >
                      {generatingWhiteLabel ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download className="h-5 w-5 mr-2" />
                          Download White Label PDF
                        </>
                      )}
                    </button>
                  </div>

                  {/* Right side - PDF Preview */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">PDF Preview</h4>
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm max-h-[600px] overflow-y-auto">
                      {/* Professional Header */}
                      <div className="bg-slate-900 text-white px-6 py-8">
                        <div className="flex items-center space-x-4 mb-4">
                          {profile?.companyLogoUrl && (
                            <img
                              src={profile.companyLogoUrl}
                              alt="Logo"
                              className="w-16 h-16 object-contain bg-white rounded p-1"
                            />
                          )}
                          <div>
                            <h1 className="text-xl font-bold">{profile?.legalBusinessName || 'BOOKING CONFIRMATION'}</h1>
                            <div className="text-slate-300 text-xs mt-1">
                              {profile?.businessAddress && <p>{profile.businessAddress}</p>}
                              {profile?.businessPhoneNumber && <p>Tel: {profile.businessPhoneNumber}</p>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                          <div className="text-slate-300 text-xs">
                            <p>Booking Date: {formatDate(selectedBookingForWhiteLabel.timestamp || selectedBookingForWhiteLabel.createdAt)}</p>
                            <p>Document Generated: {new Date().toLocaleDateString()}</p>
                          </div>
                          <span className={`px-3 py-1 rounded text-sm font-medium ${(selectedBookingForWhiteLabel.bookingStatus || selectedBookingForWhiteLabel.status || '').toLowerCase() === 'confirmed'
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-600 text-white'
                            }`}>
                            {(selectedBookingForWhiteLabel.bookingStatus || selectedBookingForWhiteLabel.status || 'Unknown').toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 space-y-6">
                        {/* Guest Information */}
                        <div>
                          <h3 className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-2 rounded mb-3">GUEST INFORMATION</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs font-medium uppercase">Name</p>
                              <p className="text-slate-900 font-medium">{getTravelerInfo(selectedBookingForWhiteLabel)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs font-medium uppercase">Reference</p>
                              <p className="text-slate-900 font-medium">
                                {selectedBookingForWhiteLabel.bookingReference || selectedBookingForWhiteLabel.confirmationId}
                              </p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-slate-500 text-xs font-medium uppercase">Email</p>
                              <p className="text-slate-900">{getContactInfo(selectedBookingForWhiteLabel).emails?.[0] || 'N/A'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Booking Details */}
                        {selectedBookingForWhiteLabel.kind === 'experience' ? (
                          <div>
                            <h3 className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-2 rounded mb-3">EXPERIENCE DETAILS</h3>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Experience</span>
                                <span className="text-slate-900 font-medium">{selectedBookingForWhiteLabel.experienceTitle || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Participants</span>
                                <span className="text-slate-900 font-medium">{selectedBookingForWhiteLabel.participantCount || 1}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Date</span>
                                <span className="text-slate-900 font-medium">{selectedBookingForWhiteLabel.selectedDate ? formatDate(selectedBookingForWhiteLabel.selectedDate) : 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h3 className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-2 rounded mb-3">HOTEL BOOKING DETAILS</h3>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Hotel</span>
                                <span className="text-slate-900 font-medium">{selectedBookingForWhiteLabel.generalBackupMetadata?.hotelName || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Check-in</span>
                                <span className="text-slate-900 font-medium">{formatDate(selectedBookingForWhiteLabel.generalBackupMetadata?.checkingDate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Check-out</span>
                                <span className="text-slate-900 font-medium">{formatDate(selectedBookingForWhiteLabel.generalBackupMetadata?.checkoutDate)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Price Breakdown */}
                        <div className="border-t border-slate-200 pt-4">
                          <h3 className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-2 rounded mb-3">PRICE BREAKDOWN</h3>
                          <div className="space-y-2 text-sm">
                            {selectedBookingForWhiteLabel.kind === 'experience' ? (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Experience Price ({selectedBookingForWhiteLabel.participantCount || 1} Guests)</span>
                                  <span className="text-slate-900">
                                    {(() => {
                                      const basePrice = (selectedBookingForWhiteLabel.pricePerPerson || 0) * (selectedBookingForWhiteLabel.participantCount || 1);
                                      return formatCurrency(basePrice > 0 ? basePrice : (selectedBookingForWhiteLabel.totalPrice || 0), selectedBookingForWhiteLabel.currency || 'USD');
                                    })()}
                                  </span>
                                </div>
                                {((selectedBookingForWhiteLabel.totalPrice || 0) - ((selectedBookingForWhiteLabel.pricePerPerson || 0) * (selectedBookingForWhiteLabel.participantCount || 1))) > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Taxes & Fees</span>
                                    <span className="text-slate-900">{formatCurrency((selectedBookingForWhiteLabel.totalPrice || 0) - ((selectedBookingForWhiteLabel.pricePerPerson || 0) * (selectedBookingForWhiteLabel.participantCount || 1)), selectedBookingForWhiteLabel.currency || 'USD')}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {(() => {
                                  const pb = getHotelBookingPriceBreakdown(selectedBookingForWhiteLabel)
                                  return (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-slate-600">Base amount</span>
                                        <span className="text-slate-900">{formatCurrency(pb.baseAmount, pb.currency)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-slate-600">Tax &amp; fees</span>
                                        <span className="text-slate-900">{formatCurrency(pb.taxAndFees, pb.currency)}</span>
                                      </div>
                                      {pb.markupAmount > 0 && (
                                        <div className="flex justify-between">
                                          <span className="text-slate-600">Markup</span>
                                          <span className="text-slate-900">{formatCurrency(pb.markupAmount, pb.currency)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-slate-600">Booking total</span>
                                        <span className="text-slate-900">{formatCurrency(pb.totalPayable, pb.currency)}</span>
                                      </div>
                                    </>
                                  )
                                })()}
                              </>
                            )}
                            <div className="flex justify-between">
                              <span className="text-slate-600">Service Charge</span>
                              <span className="text-slate-900">{formatCurrency(parseFloat(serviceCharge || 0), selectedBookingForWhiteLabel.currency || selectedBookingForWhiteLabel.generalBackupMetadata?.hotelTotals?.currencyCode || 'USD')}</span>
                            </div>
                            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                              <span className="font-bold text-slate-900">TOTAL DUE</span>
                              <span className="font-bold text-slate-900">
                                {formatCurrency(
                                  (selectedBookingForWhiteLabel.kind === 'experience'
                                    ? (selectedBookingForWhiteLabel.totalPrice || 0)
                                    : getHotelBookingPriceBreakdown(selectedBookingForWhiteLabel).totalPayable) +
                                    parseFloat(serviceCharge || 0),
                                  selectedBookingForWhiteLabel.kind === 'experience'
                                    ? (selectedBookingForWhiteLabel.currency || 'USD')
                                    : getHotelBookingPriceBreakdown(selectedBookingForWhiteLabel).currency
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Important Information */}
                        <div className="border-t border-slate-200 pt-4 text-xs text-slate-600">
                          <p>This document is prepared by your travel agent on behalf of the service provider.</p>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="bg-slate-900 text-slate-300 px-6 py-4 text-center text-xs">
                        <p>This is a confidential document provided by your travel agent</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  )
}

export default UserBookingPage